import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Select, { components as selectComponents } from 'react-select';
import '../styles/StudentsTab.css';
import MessageBox from './MessageBox';
import ConfirmDialog from './ConfirmDialog';
import ZoomableImage from './ZoomableImage';
import { copyToClipboard } from '../utils/clipboard';
import { folderDisplayTitle } from '../utils/folderDisplay';
import { useAuth } from '../contexts/AuthContext';
import {
  GRADE_BUTTONS,
  classSectionTokens,
  itemsInClass,
  itemMatchesClass,
  ClassSectionFilters,
  SubjectColumn
} from './ClassFolderSearch';

// True when a student's marks exceed the total — parsed from the
// "Total marks: X/Y" footer in their comment (Y = total). Used to flag an
// over-max mark by highlighting the marks cell red.
const marksExceedTotal = (marks, comments) => {
  const obtained = parseFloat(marks);
  if (isNaN(obtained)) return false;
  const m = String(comments || '').match(/Total\s*marks\s*:?\s*[\d.]+\s*\/\s*([\d.]+)/i);
  if (!m) return false;
  const total = parseFloat(m[1]);
  return !isNaN(total) && total > 0 && obtained > total;
};

// A student is "absent" when they have no real grading comment — the comment
// is empty, "-", or the literal "Absent" placeholder the Mark-Absent /
// fill-empty flows write. Drives the Points column ('a') and the EduPage copy.
const isStudentAbsent = (s) => {
  const c = (s && s.comments != null ? String(s.comments) : '').trim();
  return c === '' || c === '-' || c.toLowerCase() === 'absent';
};

// A student was PLACEHOLDER-marked absent by the Mark-Absent flow when their
// first name is the "." placeholder AND the comment is the literal "Absent".
// Stricter than isStudentAbsent on purpose: "Unmark Absent" should only undo
// rows that button actually wrote, never a genuinely-blank row the user never
// touched (first name empty, no comment).
const isAbsentMarked = (s) => {
  const fn = (s && s.first_name != null ? String(s.first_name) : '').trim();
  const c = (s && s.comments != null ? String(s.comments) : '').trim();
  // first name "." is the real gate (a graded student always has their actual
  // name here); the comment just has to contain "Absent".
  return fn === '.' && c.toLowerCase().includes('absent');
};

const formatHistoryDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

// One line per approve click. Rows arrive newest first, so the first time a
// batch is seen is the order the list should show.
const groupApprovalHistory = (rows) => {
  const batches = [];
  const indexByBatch = new Map();
  (rows || []).forEach((row) => {
    const key = row.approval_batch || `${row.folder_path}|${row.approved_at}`;
    let batch = indexByBatch.get(key);
    if (!batch) {
      batch = {
        batch: key,
        folder_name: row.folder_name || row.folder_path || 'Folder',
        assignment_name: row.assignment_name || '',
        approved_at: row.approved_at,
        students: []
      };
      indexByBatch.set(key, batch);
      batches.push(batch);
    }
    batch.students.push(row);
  });
  batches.forEach((batch) => {
    batch.students.sort((a, b) =>
      String(a.student_name || '').localeCompare(String(b.student_name || ''), undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    );
  });
  return batches;
};

function compareStudentLists(a, b) {
  const ta = classSectionTokens(a.class);
  const tb = classSectionTokens(b.class);
  const rank = (tokens) => {
    const grade = tokens[0] && tokens[0].grade;
    const i = GRADE_BUTTONS.indexOf(grade);
    return i === -1 ? 999 : i;
  };
  const byGrade = rank(ta) - rank(tb);
  if (byGrade !== 0) return byGrade;
  const byCombined = (ta.length > 1 ? 1 : 0) - (tb.length > 1 ? 1 : 0);
  if (byCombined !== 0) return byCombined;
  const letters = (tokens) => tokens.map(t => t.letter || '').sort().join('');
  const byLetter = letters(ta).localeCompare(letters(tb));
  if (byLetter !== 0) return byLetter;
  const head = (cls) => String(cls || '').split(/\s+-\s+/)[0];
  const byHead = head(a.class).localeCompare(head(b.class), undefined, { numeric: true });
  if (byHead !== 0) return byHead;
  return String(a.subject || '').localeCompare(String(b.subject || ''), undefined, { sensitivity: 'base' });
}

const StudentsTab = ({ token }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  // Teachers may browse student lists but cannot import, edit, or delete.
  const isReadOnly = currentUser?.role === 'teacher';
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Inline-edit state for the student list table. Double-click a cell to
  // enter edit mode; Enter / blur commits, Escape cancels. One cell at a
  // time. Matches the editing pattern used in AnalyzeTab / AssessmentTab
  // for consistency.
  const [editingCell, setEditingCell] = useState(null); // { studentId, field, value }
  const [faultCorrections, setFaultCorrections] = useState({});
  const [faultStudents, setFaultStudents] = useState([]);
  const [doneClicked, setDoneClicked] = useState(false);
  const [copyClicked, setCopyClicked] = useState(false);
  const [saveClicked, setSaveClicked] = useState(false);
  const [showDoneModal, setShowDoneModal] = useState(false);
  // "View All Comments" modal — lists every student's comment at once, like
  // the button in the Checking (Assessment) tab.
  const [showAllComments, setShowAllComments] = useState(false);
  const [scansFolders, setScansFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [newStudent, setNewStudent] = useState({ student_name: '', first_name: '', marks: '', comments: '' });
  const [messages, setMessages] = useState([]);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const [message, setMessage] = useState({ show: false, text: '', type: 'info' });
  const [confirm, setConfirm] = useState({ show: false, text: '', onConfirm: null });
  const [faultPreviewImage, setFaultPreviewImage] = useState(null); // { images: [{url, name}], index }
  const [faultPreviewLoading, setFaultPreviewLoading] = useState(null);

  useEffect(() => {
    if (!faultPreviewImage || !faultPreviewImage.images) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setFaultPreviewImage(null);
      } else if (e.key === 'ArrowLeft') {
        setFaultPreviewImage(p => (p && p.index > 0) ? { ...p, index: p.index - 1 } : p);
      } else if (e.key === 'ArrowRight') {
        setFaultPreviewImage(p => (p && p.index < p.images.length - 1) ? { ...p, index: p.index + 1 } : p);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [faultPreviewImage]);
  const [currentDateOffset, setCurrentDateOffset] = useState(0); // 0 = today, -1 = yesterday, +1 = tomorrow
  const [listCourseFilter, setListCourseFilter] = useState([]); // Course (class+subject) multi-filter for student lists
  const [gradeFilter, setGradeFilter] = useState(null); // PG | N | Pr | '1'..'12'
  const [sectionFilter, setSectionFilter] = useState(null); // e.g. '1C', '12A'
  const [subjectFilter, setSubjectFilter] = useState(null);
  const [courseFilterInput, setCourseFilterInput] = useState(''); // Persisted search text for course filter
  const [selectedCardKeys, setSelectedCardKeys] = useState([]); // Keys of selected list cards for bulk delete
  const [lastApprovedLabel, setLastApprovedLabel] = useState(() => localStorage.getItem('lastApprovedAssessmentLabel') || '');
  // Teacher accounts (name + email) so Login on an opened list can match
  // "Naheed.Akhtar" to the EduPage address the extension actually logs in with.
  const [teacherAccounts, setTeacherAccounts] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyBatches, setHistoryBatches] = useState([]);
  const [historyError, setHistoryError] = useState('');
  const [selectedHistoryBatch, setSelectedHistoryBatch] = useState(null);
  const [historyCopied, setHistoryCopied] = useState(false);
  const [historyAbsentBusy, setHistoryAbsentBusy] = useState(false);
  // Login Edupage extension bridge — same pattern as Sort / Check Login.
  const [eduBridgeReady, setEduBridgeReady] = useState(false);
  const [folderSearchInput, setFolderSearchInput] = useState(''); // Search input for folder selector modal
  
  // Quick Add/Remove student states
  const [quickStudentName, setQuickStudentName] = useState('');
  const [quickClassPrefix, setQuickClassPrefix] = useState('');
  const [quickActionLoading, setQuickActionLoading] = useState(false);
  const [showAddToListsModal, setShowAddToListsModal] = useState(false);
  const [showRemoveFromListsModal, setShowRemoveFromListsModal] = useState(false);
  const [selectedListsForAction, setSelectedListsForAction] = useState([]);
  const [modalListFilter, setModalListFilter] = useState(''); // search filter inside add/remove modals
  
  // Automated flow states
  const [isAutomating, setIsAutomating] = useState(false);
  const [automationStep, setAutomationStep] = useState(''); // 'saving', 'processing', 'copying', 'complete'
  const [automationProgress, setAutomationProgress] = useState({
    saving: 'pending',      // 'pending', 'inProgress', 'success', 'error'
    processing: 'pending',
    copying: 'pending'
  });
  const [countdown, setCountdown] = useState(5);
  
  // Form data for import - now supports multiple files
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  
  // Non-standard file import support
  const [nonStandardFile, setNonStandardFile] = useState(false);
  const [convertedStudents, setConvertedStudents] = useState([]);
  const [manualMetadata, setManualMetadata] = useState({ class: '', section: '', subject: '', teacher: '' });
  const [parsedHtmlLists, setParsedHtmlLists] = useState([]); // Multiple lists from a single HTML-XLS file
  
  const getErrorMessage = (err, fallback = 'Something went wrong') => {
    if (!err) return fallback;
    if (typeof err === 'string') return err;
    if (err.message && typeof err.message === 'string') return err.message;
    if (err.error) {
      if (typeof err.error === 'string') return err.error;
      if (err.error.message && typeof err.error.message === 'string') return err.error.message;
    }
    if (err.details && typeof err.details === 'string') return err.details;
    try {
      const serialized = JSON.stringify(err);
      return serialized && serialized !== '{}' ? serialized : fallback;
    } catch (_) {
      return fallback;
    }
  };

  // Helper function to format folder display label.
  // Desired format: "<folder.title> <date> (<N> images)" — preserving any ' - '
  // already inside folder.title (e.g. "9A+9B - comp - computer-a"). MCQs
  // folders get " MCQs" appended via folderDisplayTitle for parity with
  // every other folder card across the app.
  const formatFolderLabel = (folder) => {
    const dateStr = new Date(folder.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    return `${folderDisplayTitle(folder)} ${dateStr} (${folder.scan_count} images)`;
  };

  // Build the display label for a student list: "<class> - <subject>".
  // The raw class value (e.g. "9A+9B - comp") is kept intact so the final label
  // reads like "9A+9B - comp - computer-a".
  const formatListLabel = (cls, subject) => {
    const c = (cls || '').trim();
    const s = (subject || '').trim();
    if (c && s) return `${c} - ${s}`;
    return c || s;
  };

  // Helper function to format checked date (convert to Pakistani time)
  const formatCheckedDate = (dateString, list = null) => {
    // Try the provided dateString first
    if (dateString) {
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            timeZone: 'Asia/Karachi'
          });
        }
      } catch (err) {
        // Continue to fallback
      }
    }
    
    // Fallback: try checked_time if list is provided and it contains a full datetime
    if (list && list.checked_time && list.checked_time.includes('T')) {
      try {
        const date = new Date(list.checked_time);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            timeZone: 'Asia/Karachi'
          });
        }
      } catch (err) {
        // Continue to fallback
      }
    }
    
    // Fallback: try created_at if list is provided
    if (list && list.created_at) {
      try {
        const date = new Date(list.created_at);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            timeZone: 'Asia/Karachi'
          });
        }
      } catch (err) {
        // No valid date
      }
    }
    
    return 'N/A';
  };

  // Helper function to format checked time (convert to Pakistani time)
  const formatCheckedTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      // If timeString is already just time (HH:MM:SS or HH:MM)
      if (timeString.includes(':') && !timeString.includes('T')) {
        // Assume it's already in PKT format
        const [hours, minutes] = timeString.split(':');
        return `${hours}:${minutes}`;
      }
      // If it's an ISO datetime string, convert to PKT
      const date = new Date(timeString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false,
          timeZone: 'Asia/Karachi'
        });
      }
      return timeString;
    } catch (err) {
      return 'N/A';
    }
  };

  // Fetch all student lists and messages on mount
  useEffect(() => {
    fetchLists();
    fetchScansFolders();
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open a student list when navigated from Approve (Evaluate tab)
  useEffect(() => {
    // Refresh last approved label from localStorage whenever we arrive here.
    // Still used to pre-fill the Done folder search; the opened-list card no
    // longer shows it.
    const stored = localStorage.getItem('lastApprovedAssessmentLabel');
    if (stored) setLastApprovedLabel(stored);

    if (location.state?.autoOpenList && lists.length > 0) {
      const { class: cls, section, subject } = location.state.autoOpenList;
      const lc = (v) => String(v == null ? '' : v).toLowerCase();
      // Prefer an exact class+section+subject match when a section is given
      // (e.g. coming from Approve). Folder cards carry no section, so fall
      // back to class+subject (exact, then case-insensitive).
      let matchingList = null;
      if (section != null && section !== '') {
        matchingList = lists.find(
          (l) => l.class === cls && l.section === section && l.subject === subject
        );
      }
      if (!matchingList) {
        matchingList = lists.find((l) => l.class === cls && l.subject === subject)
          || lists.find((l) => lc(l.class) === lc(cls) && lc(l.subject) === lc(subject));
      }
      if (matchingList) {
        handleViewList(matchingList);
      }
      // Clear the navigation state so it doesn't re-trigger
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, lists]);

  // ── Login Edupage extension bridge ──────────────────────────────────────
  // "Login" on the opened class card hands that class to the Login Edupage
  // extension (matches teacher email → auto-login, then opens the course).
  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.source !== window) return;
      const d = ev.data;
      if (!d || typeof d !== 'object' || d.source !== 'edupage-selector') return;
      if (d.type === 'EDU_BRIDGE_READY') {
        setEduBridgeReady(true);
      } else       if (d.type === 'EDU_LOGIN_RESULT' && !d.ok) {
        setMessage({
          show: true,
          text: d.reason === 'not_found'
            ? "EduPage login: this teacher's email isn't in the extension's credential list."
            : 'EduPage login failed — is the Login Edupage extension installed and updated?',
          type: 'error'
        });
      }
    };
    window.addEventListener('message', onMsg);
    window.postMessage({ source: 'fastgrader-edu', type: 'EDU_BRIDGE_PING' }, '*');
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // Examiners log in as the class teacher. The list stores a display name
  // ("Naheed.Akhtar"); the extension matches an email. Load the teacher
  // roster so the name can be turned back into that email. A teacher viewing
  // their own list only needs their own account.
  useEffect(() => {
    const mine = [];
    if (currentUser && (currentUser.email || currentUser.username)) {
      mine.push({
        username: currentUser.username || '',
        full_name: currentUser.name || currentUser.full_name || '',
        email: currentUser.email || ''
      });
    }
    const elevated = currentUser && ['examiner', 'administrator', 'management'].includes(currentUser.role);
    if (!elevated) {
      setTeacherAccounts(mine);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${window.API_BASE_URL}/auth/teachers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data.teachers) ? data.teachers : [];
          if (!cancelled) setTeacherAccounts(list.length ? list : mine);
          return;
        }
      } catch (_) { /* roster optional — folder email is the fallback */ }
      if (!cancelled) setTeacherAccounts(mine);
    })();
    return () => { cancelled = true; };
  }, [currentUser, token]);
  
  // Pre-fill folder search when modal opens
  useEffect(() => {
    if (showDoneModal) {
      setSelectedFolder('');
      // Auto-type the last approved label so matching folders appear immediately
      setFolderSearchInput(lastApprovedLabel || '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDoneModal]);

  useEffect(() => {
    setSelectedStudentIds(prev => prev.filter(id => students.some(s => s.id === id)));
  }, [students]);
  
  // Get target date based on offset
  const getTargetDate = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + currentDateOffset);
    return date;
  };
  
  // Get filtered folders for current date offset
  const getFilteredFolders = () => {
    if (!scansFolders || scansFolders.length === 0) return [];
    
    const targetDate = getTargetDate();
    const targetTime = targetDate.getTime();
    
    // First filter by date
    const filteredByDate = scansFolders.filter(f => {
      const folderDate = new Date(f.test_date);
      folderDate.setHours(0, 0, 0, 0);
      return folderDate.getTime() === targetTime;
    });

    // Then sort using natural sort on title
    return filteredByDate.sort((a, b) => {
      const titleA = a.title || '';
      const titleB = b.title || '';

      // Use localeCompare with numeric option for natural sorting
      return titleA.localeCompare(titleB, undefined, { 
        numeric: true, 
        sensitivity: 'base' 
      });
    });
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudentIds(prev =>
      prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]
    );
  };

  const handleSelectAllStudents = () => {
    if (students.length === 0) return;
    if (selectedStudentIds.length === students.length) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(students.map(s => s.id));
    }
  };

  const handleDeleteSelectedStudents = async () => {
    if (isReadOnly) return;
    if (selectedStudentIds.length === 0) return;
    showConfirm(`Delete ${selectedStudentIds.length} selected student(s)?`, async () => {
      try {
        let deleted = 0;
        for (const studentId of selectedStudentIds) {
          const response = await fetch(`${window.API_BASE_URL}/students/${studentId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) deleted++;
        }
        setSuccess(`Deleted ${deleted} student(s)`);
        setSelectedStudentIds([]);
        if (selectedList) {
          await fetchStudents(selectedList.class, selectedList.section, selectedList.subject);
        }
        await fetchLists();
      } catch (err) {
        setError('Failed to delete selected students');
      }
    });
  };
  
  // Check if there are folders available for previous/next dates
  const hasPreviousDate = () => {
    if (!scansFolders || scansFolders.length === 0) return false;
    const currentTime = getTargetDate().getTime();
    // Check if any folder exists with a date earlier than current
    return scansFolders.some(f => {
      const folderDate = new Date(f.test_date);
      folderDate.setHours(0, 0, 0, 0);
      return folderDate.getTime() < currentTime;
    });
  };
  
  const hasNextDate = () => {
    if (!scansFolders || scansFolders.length === 0) return false;
    const currentTime = getTargetDate().getTime();
    // Check if any folder exists with a date later than current
    return scansFolders.some(f => {
      const folderDate = new Date(f.test_date);
      folderDate.setHours(0, 0, 0, 0);
      return folderDate.getTime() > currentTime;
    });
  };
  
  // Navigate to previous date with folders
  const goToPreviousDate = () => {
    if (!scansFolders || scansFolders.length === 0) return;
    const currentTime = getTargetDate().getTime();
    
    // Get all folders before current date, sorted by date (newest first)
    const previousFolders = scansFolders
      .filter(f => {
        const folderDate = new Date(f.test_date);
        folderDate.setHours(0, 0, 0, 0);
        return folderDate.getTime() < currentTime;
      })
      .sort((a, b) => {
        const dateA = new Date(a.test_date);
        const dateB = new Date(b.test_date);
        return dateB - dateA; // Newest first
      });
    
    if (previousFolders.length > 0) {
      // Get the most recent folder before current date
      const targetFolder = previousFolders[0];
      const targetDate = new Date(targetFolder.test_date);
      targetDate.setHours(0, 0, 0, 0);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Calculate offset from today
      const diffTime = targetDate.getTime() - today.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
      
      setCurrentDateOffset(diffDays);
      setSelectedFolder(''); // Reset folder selection
    }
  };
  
  // Navigate to next date with folders
  const goToNextDate = () => {
    if (!scansFolders || scansFolders.length === 0) return;
    const currentTime = getTargetDate().getTime();
    
    // Get all folders after current date, sorted by date (oldest first)
    const nextFolders = scansFolders
      .filter(f => {
        const folderDate = new Date(f.test_date);
        folderDate.setHours(0, 0, 0, 0);
        return folderDate.getTime() > currentTime;
      })
      .sort((a, b) => {
        const dateA = new Date(a.test_date);
        const dateB = new Date(b.test_date);
        return dateA - dateB; // Oldest first
      });
    
    if (nextFolders.length > 0) {
      // Get the earliest folder after current date
      const targetFolder = nextFolders[0];
      const targetDate = new Date(targetFolder.test_date);
      targetDate.setHours(0, 0, 0, 0);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Calculate offset from today
      const diffTime = targetDate.getTime() - today.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
      
      setCurrentDateOffset(diffDays);
      setSelectedFolder(''); // Reset folder selection
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${window.API_BASE_URL}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Students banner is for public announcements only — hide private recheck notices.
        setMessages((data.messages || []).filter((m) => m.recipient_user_id == null));
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  };

  const fetchLists = async () => {
    try {
      const response = await fetch(`${window.API_BASE_URL}/students/lists`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Deduplicate lists by class+section+subject (backend may return duplicates after adding students)
        const seen = new Set();
        const uniqueLists = (data || []).filter(list => {
          const key = `${list.class}|${list.section}|${list.subject}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        // Apply natural sort to student lists
        const sortedLists = uniqueLists.sort((a, b) => {
          // Create sortable strings from class-section subject
          const titleA = `${a.class || ''}-${a.section || ''} ${a.subject || ''}`;
          const titleB = `${b.class || ''}-${b.section || ''} ${b.subject || ''}`;

          // Use localeCompare with numeric option for natural sorting
          return titleA.localeCompare(titleB, undefined, { 
            numeric: true, 
            sensitivity: 'base' 
          });
        });
        setLists(sortedLists);
      } else {
        throw new Error('Failed to fetch lists');
      }
    } catch (err) {

      setError('Failed to load student lists');
    }
  };

  const fetchScansFolders = async () => {
    try {
      const response = await fetch(`${window.API_BASE_URL}/scans/folders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Subjective-only: this folder picker is for the student-approval
        // workflow, which is tied to subjective grading. MCQ folders have
        // their own approval path under MCQs > Approve & Match.
        const subjectiveFolders = (data.folders || []).filter(f => f.type !== 'mcq');
        // Sort folders by date (newest first)
        const sortedFolders = subjectiveFolders.sort((a, b) => {
          const dateA = new Date(a.test_date || a.created_at || 0);
          const dateB = new Date(b.test_date || b.created_at || 0);
          return dateB - dateA;
        });
        setScansFolders(sortedFolders);
      }
    } catch (err) {
      console.error('Failed to load scans folders:', err);
      setScansFolders([]);
    }
  };

  const fetchStudents = async (className, section, subject) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(
        `${window.API_BASE_URL}/students?class=${encodeURIComponent(className)}&section=${encodeURIComponent(section)}&subject=${encodeURIComponent(subject)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setStudents(data);
        
        // Fetch fault students (unmatched from Tables tab)
        await fetchFaultStudents(className, section, subject);
      } else {
        throw new Error('Failed to fetch students');
      }
    } catch (err) {

      setError('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const openListHistory = async () => {
    if (!selectedList) return;
    setShowHistory(true);
    setSelectedHistoryBatch(null);
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const params = new URLSearchParams({
        class: selectedList.class || '',
        section: selectedList.section || '',
        subject: selectedList.subject || ''
      });
      const response = await fetch(`${window.API_BASE_URL}/students/history?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load history');
      const data = await response.json();
      setHistoryBatches(groupApprovalHistory(Array.isArray(data) ? data : []));
    } catch (err) {
      console.error('Failed to load assignment history:', err);
      setHistoryError('Could not load approval history.');
      setHistoryBatches([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Every student on the open list, in list order. Students who were not in
  // this approval stay in the table as empty rows, the same way the live
  // list shows a student before Mark Absent.
  const historyRowsInListOrder = (batch) => {
    const rows = (batch && batch.students) || [];
    const byId = new Map();
    const byName = new Map();
    rows.forEach((row) => {
      if (row.student_list_id != null) byId.set(Number(row.student_list_id), row);
      byName.set(String(row.student_name || ''), row);
    });
    const ordered = [];
    const used = new Set();
    students.forEach((student) => {
      const row = byId.get(Number(student.id)) || byName.get(String(student.student_name || ''));
      if (row && !used.has(row.id)) {
        ordered.push(row);
        used.add(row.id);
      } else {
        ordered.push({
          id: `missing-${student.id}`,
          student_list_id: student.id,
          student_name: student.student_name,
          first_name: '',
          marks: '',
          comments: '',
          missing: true
        });
      }
    });
    rows.forEach((row) => {
      if (!used.has(row.id)) ordered.push(row);
    });
    return ordered;
  };

  const historyCheckedName = (row) => {
    const checked = row && row.first_name != null ? String(row.first_name).trim() : '';
    if (checked) return checked;
    // Approvals saved before first_name was stored still have marks.
    if (row && !row.missing && (row.marks || row.comments)) return row.student_name || '';
    return '';
  };

  const historyRowNeedsAbsent = (row) => {
    const firstName = String((row && row.first_name) || '').trim();
    const marks = String(row && row.marks != null ? row.marks : '').trim();
    const comments = String((row && row.comments) || '').trim();
    if (row && row.missing) return true;
    return !firstName || !marks || marks === '-' || !comments || comments === '-';
  };

  const handleCopyHistoryMarks = async () => {
    if (!selectedHistoryBatch) return;
    const marksData = historyRowsInListOrder(selectedHistoryBatch)
      .map((row) => row.marks || '-')
      .join('\n');
    const ok = await copyToClipboard(marksData);
    if (!ok) {
      setHistoryError('Failed to copy marks to clipboard');
      return;
    }
    setHistoryCopied(true);
    setTimeout(() => setHistoryCopied(false), 2000);
  };

  const handleMarkHistoryAbsent = async () => {
    if (isReadOnly || !selectedList || !selectedHistoryBatch || historyAbsentBusy) return;
    setHistoryAbsentBusy(true);
    setHistoryError('');
    try {
      const response = await fetch(`${window.API_BASE_URL}/students/history/mark-absent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          class: selectedList.class,
          section: selectedList.section,
          subject: selectedList.subject,
          approval_batch: selectedHistoryBatch.batch
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to mark absent');
      const updated = groupApprovalHistory(Array.isArray(data.rows) ? data.rows : []);
      const next = updated.find((batch) => batch.batch === selectedHistoryBatch.batch) || updated[0];
      if (next) {
        setSelectedHistoryBatch(next);
        setHistoryBatches((prev) => prev.map((batch) => (
          batch.batch === next.batch ? next : batch
        )));
      }
    } catch (err) {
      console.error('Failed to mark history absent:', err);
      setHistoryError(err.message || 'Could not mark absent students.');
    } finally {
      setHistoryAbsentBusy(false);
    }
  };

  const handleCopyHistoryResults = () => {
    if (isReadOnly || !selectedHistoryBatch) return;
    const rows = historyRowsInListOrder(selectedHistoryBatch).filter((row) => historyCheckedName(row));
    if (rows.length === 0) return;
    const data = rows.map((row) => {
      const marks = (row.marks && row.marks !== '-') ? row.marks : '.';
      const comments = (row.comments && row.comments !== '-') ? row.comments : 'Absent';
      const points = isStudentAbsent(row) ? 'a' : '';
      return `${marks}\t${comments}\t${points}`;
    }).join('\n');
    copyToClipboard(data).then((ok) => {
      if (!ok) {
        setHistoryError('Failed to copy to clipboard');
        return;
      }
      setHistoryCopied(true);
      setTimeout(() => setHistoryCopied(false), 2000);
      window.postMessage({ type: 'FG_MARKS_TO_EDUPAGE', data }, '*');
    });
  };

  const fetchFaultStudents = async (className, section, subject) => {
    try {
      const response = await fetch(
        `${window.API_BASE_URL}/students/faults?class=${encodeURIComponent(className)}&section=${encodeURIComponent(section)}&subject=${encodeURIComponent(subject)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        const faults = data.faults || [];
        console.log('Fetched fault students:', faults);
        if (faults.length > 0) {
          faults.forEach((f, i) => {
            console.log(`Fault ${i}:`, {
              id: f.id,
              name: f.name,
              student_name: f.student_name,
              comments: f.comments ? f.comments.substring(0, 50) : 'null',
              created_at: f.created_at
            });
          });
        }
        console.log('Updating faultStudents state with', faults.length, 'records');
        setFaultStudents(faults);
      }
    } catch (err) {
      console.error('Error fetching fault students:', err);
    }
  };

  // Helper: parse class·subject string into { class, section, subject }
  // Class is kept as-is (e.g. "1C", "9A+9B") — no separate section extracted
  const parseClassSubject = (metadataStr) => {
    const metaParts = metadataStr.split(/[··]/).map(s => s.trim());
    return {
      class: metaParts[0] || '',
      section: '',
      subject: metaParts[1] || ''
    };
  };

  // Parse HTML-disguised-as-XLS files using DOMParser
  // Supports multiple list blocks in a single file
  const parseHtmlXls = (htmlContent, selectedFiles) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    // Strategy: find all class·subject spans, then for each find the associated teacher and student table
    // The HTML structure repeats: school-name table → section(class·subject span + teacher span) → student table
    const allSpans = doc.querySelectorAll('section > div > span');
    const metadataEntries = []; // { metadata, teacher, element }
    
    for (let i = 0; i < allSpans.length; i++) {
      const text = allSpans[i].textContent.trim();
      if (text.includes('·') || text.includes('·')) {
        // This is a class·subject line; the next span is typically the teacher
        const teacher = (i + 1 < allSpans.length) ? allSpans[i + 1].textContent.trim() : '';
        metadataEntries.push({
          metadata: text,
          teacher: teacher,
          element: allSpans[i]
        });
      }
    }
    
    if (metadataEntries.length === 0) return false;
    
    // For each metadata entry, find the student table that follows it
    // Walk up to the parent section, then find the next sibling section with a table
    const detectedLists = [];
    
    metadataEntries.forEach((entry) => {
      // Find the closest parent section that contains this span
      let parentSection = entry.element.closest('section');
      if (!parentSection) return;
      
      // The student table is in the next sibling section
      let nextSection = parentSection.nextElementSibling;
      while (nextSection && !nextSection.querySelector('tbody')) {
        nextSection = nextSection.nextElementSibling;
      }
      
      if (!nextSection) return;
      
      const studentNames = [];
      // Only extract from tables that have a thead (student tables)
      // The school-name table has NO thead — this prevents it from being included
      const studentTables = nextSection.querySelectorAll('table');
      studentTables.forEach(table => {
        if (!table.querySelector('thead')) return; // skip tables without header
        const rows = table.querySelectorAll('tbody tr');
        rows.forEach(row => {
          const cells = row.querySelectorAll('td');
          if (cells.length >= 2) {
            const nameCell = cells[1].textContent.trim();
            if (nameCell && isNaN(nameCell)) {
              studentNames.push(nameCell);
            }
          }
        });
      });
      
      // Fallback: if no thead-table found, use all tbody rows but filter school-like names
      if (studentNames.length === 0) {
        const rows = nextSection.querySelectorAll('tbody tr');
        rows.forEach(row => {
          const cells = row.querySelectorAll('td');
          if (cells.length >= 2) {
            const nameCell = cells[1].textContent.trim();
            if (nameCell && isNaN(nameCell) && !nameCell.toUpperCase().includes('SCHOOL') && !nameCell.toUpperCase().includes('TOWNSHIP')) {
              studentNames.push(nameCell);
            }
          }
        });
      }
      
      if (studentNames.length > 0) {
        const parsed = parseClassSubject(entry.metadata);
        detectedLists.push({
          metadata: entry.metadata,
          class: parsed.class,
          section: parsed.section,
          subject: parsed.subject,
          teacher: entry.teacher,
          students: studentNames
        });
      }
    });
    
    if (detectedLists.length === 0) return false;
    
    // Store all detected lists
    setParsedHtmlLists(detectedLists);
    setNonStandardFile(true);
    
    if (detectedLists.length === 1) {
      // Single list: use existing single-list flow with auto-fill
      const list = detectedLists[0];
      setConvertedStudents(list.students);
      setManualMetadata({
        class: list.class,
        section: list.section,
        subject: list.subject,
        teacher: list.teacher
      });
      setPreviewData({
        metadata: list.metadata,
        teacherNames: list.teacher,
        studentCount: list.students.length,
        totalFiles: selectedFiles.length,
        isNonStandard: true,
        autoFilled: true
      });
    } else {
      // Multiple lists detected
      const totalStudents = detectedLists.reduce((sum, l) => sum + l.students.length, 0);
      setPreviewData({
        metadata: `${detectedLists.length} lists detected`,
        teacherNames: '',
        studentCount: totalStudents,
        totalFiles: selectedFiles.length,
        isNonStandard: true,
        autoFilled: true,
        multiList: true,
        listCount: detectedLists.length
      });
    }
    
    return true;
  };

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;
    
    // Store all selected files
    setFiles(selectedFiles);
    setFile(selectedFiles[0]); // Keep first file as primary for backward compatibility
    setNonStandardFile(false);
    setConvertedStudents([]);
    setManualMetadata({ class: '', section: '', subject: '', teacher: '' });
    setParsedHtmlLists([]);
    
    // Preview first file's extracted data
    try {
      const firstFile = selectedFiles[0];
      
      // First, check if the file is HTML disguised as .xls
      const textContent = await firstFile.text();
      const isHtmlFile = textContent.trimStart().startsWith('<html') || textContent.trimStart().startsWith('<!DOCTYPE');
      
      if (isHtmlFile) {
        const parsed = parseHtmlXls(textContent, selectedFiles);
        if (parsed) return; // Successfully parsed as HTML
      }
      
      // Standard XLSX/XLS parsing
      const XLSX = await import('xlsx');
      const reader = new FileReader();
      
      reader.onload = (event) => {
        const workbook = XLSX.read(event.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Extract metadata
        const a2 = worksheet['A2'] ? worksheet['A2'].v : ''; // Class+Section · Subject
        const a3 = worksheet['A3'] ? worksheet['A3'].v : ''; // Teacher Names
        
        // Extract student data starting from row 6 (B6 onwards)
        const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, range: 5 }); // Start from row 6 (index 5)
        
        // Count non-empty entries in column B (index 1)
        let studentCount = 0;
        for (let i = 0; i < data.length; i++) {
          if (data[i] && data[i][1] && data[i][1].toString().trim() !== '') {
            studentCount++;
          }
        }
        
        // If standard format detected (has metadata and students), use it
        if (a2 && studentCount > 0) {
          setPreviewData({
            metadata: a2,
            teacherNames: a3,
            studentCount,
            totalFiles: selectedFiles.length
          });
          return;
        }
        
        // Non-standard format: scan all sheets for student-like data
        let extractedNames = [];
        for (const sName of workbook.SheetNames) {
          const ws = workbook.Sheets[sName];
          const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
          if (rows.length < 2) continue;
          
          // Find the column that looks like student names (skip header row)
          const colCount = Math.max(...rows.map(r => (r ? r.length : 0)));
          for (let col = 0; col < colCount; col++) {
            const colValues = [];
            for (let row = 1; row < rows.length; row++) {
              const val = rows[row] && rows[row][col] != null ? rows[row][col].toString().trim() : '';
              if (val && isNaN(val)) { // Skip pure numbers (like serial #)
                colValues.push(val);
              }
            }
            if (colValues.length > extractedNames.length) {
              extractedNames = colValues;
            }
          }
        }
        
        if (extractedNames.length > 0) {
          setNonStandardFile(true);
          setConvertedStudents(extractedNames);
          setPreviewData({
            metadata: '(Not detected — please fill below)',
            teacherNames: '',
            studentCount: extractedNames.length,
            totalFiles: selectedFiles.length,
            isNonStandard: true
          });
        } else {
          // Fallback: show standard preview with 0 students
          setPreviewData({
            metadata: a2,
            teacherNames: a3,
            studentCount: 0,
            totalFiles: selectedFiles.length
          });
        }
      };
      
      reader.readAsBinaryString(firstFile);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to read files'));
    }
  };

  const handleImportMultipleFiles = async () => {
    if (isReadOnly) return;
    if (files.length === 0) return;
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let totalImported = 0;
      let totalFailed = 0;
      let importedLists = [];

      for (const currentFile of files) {
        try {
          const formDataToSend = new FormData();
          formDataToSend.append('file', currentFile);

          const response = await fetch(`${window.API_BASE_URL}/students/import`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formDataToSend
          });

          if (response.ok) {
            const result = await response.json();
            const countMsg = result.totalStudents ? result.totalStudents : result.count;
            totalImported += countMsg;
            importedLists.push(`${currentFile.name}: ${countMsg} students`);
          } else {
            try {
              const errorData = await response.json();
              console.error('Import error:', errorData);
            } catch (_) {}
            totalFailed++;
          }
        } catch (err) {
          totalFailed++;
        }
      }

      if (totalImported > 0) {
        setSuccess(`✅ Successfully imported ${totalImported} total students from ${files.length - totalFailed} file(s)`);
        
        // Reset form
        setFile(null);
        setFiles([]);
        setPreviewData(null);
        document.getElementById('file-input').value = '';
        
        // Refresh lists
        await fetchLists();
      } else {
        setError('Failed to import files');
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to import files'));
    } finally {
      setLoading(false);
    }
  };

  // Build a converted .xlsx file from student list data
  const buildConvertedFile = async (listData = null) => {
    const XLSX = await import('xlsx');
    const wb = XLSX.utils.book_new();
    const wsData = [];
    
    const meta = listData || { class: manualMetadata.class, section: manualMetadata.section, subject: manualMetadata.subject, teacher: manualMetadata.teacher, students: convertedStudents, metadata: null };
    
    // Row 1: Header
    wsData.push(['Student List']);
    // Row 2 (A2): Use original metadata string if available (e.g. "1C · GAME")
    // We intentionally omit the auto-extracted section from the label because
    // it is often just the trailing letter of the subject (e.g. "-a").
    const a2Value = meta.metadata || `${meta.class} · ${meta.subject}`;
    wsData.push([a2Value]);
    // Row 3 (A3): Teacher
    wsData.push([meta.teacher || '']);
    // Row 4: empty
    wsData.push([]);
    // Row 5: column headers
    wsData.push(['#', 'Student Name']);
    // Row 6+: students
    meta.students.forEach((name, idx) => {
      wsData.push([idx + 1, name]);
    });
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    
    const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbOut], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    return new File([blob], 'converted_students.xlsx', { type: blob.type });
  };

  const handleSave = async () => {
    if (isReadOnly) return;
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!file) {
        setError('Please select a file');
        setLoading(false);
        return;
      }
      
      // Multi-list import from single HTML-XLS file
      if (nonStandardFile && previewData && previewData.multiList && parsedHtmlLists.length > 1) {
        await importMultipleHtmlLists();
        return;
      }
      
      // Validate manual metadata for single non-standard files
      if (nonStandardFile && !(previewData && previewData.multiList)) {
      if (!manualMetadata.class || !manualMetadata.subject) {
          setError('Please fill in Class and Subject fields for this file.');
          setLoading(false);
          return;
        }
      }
      
      // Warn if no students detected in preview
      if (previewData && previewData.studentCount === 0) {
        showConfirm('Preview shows 0 students. This might mean the Excel format is incorrect. Do you want to continue anyway?', async () => {
          await continueImport();
        });
        setLoading(false);
        return;
      }
      
      await continueImport();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Import multiple lists from a single HTML-XLS file
  const importMultipleHtmlLists = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Check for duplicates upfront
      // Compare by subject + class (stripping section suffix since backend may store it differently)
      const normalizeClass = (cls) => cls.toLowerCase().replace(/[\s-]/g, '');
      const duplicates = parsedHtmlLists.filter(list =>
        lists.some(existing =>
          normalizeClass(existing.class).includes(normalizeClass(list.class)) ||
          normalizeClass(list.class).includes(normalizeClass(existing.class))
            ? existing.subject.toLowerCase().replace(/\s/g, '') === list.subject.toLowerCase().replace(/\s/g, '')
            : false
        )
      );
      
      if (duplicates.length > 0) {
        const dupNames = duplicates.map(d => formatListLabel(d.class, d.subject)).join(', ');
        const userConfirmed = await new Promise(resolve => {
          setConfirm({
            show: true,
            text: `The following list(s) already exist:\n\n${dupNames}\n\nDo you want to replace them with the new data?`,
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });
        setConfirm({ show: false, text: '', onConfirm: null });
        if (!userConfirmed) {
          setLoading(false);
          return;
        }
        // Delete ALL copies of each duplicate (not just the first) before re-importing
        for (const dup of duplicates) {
          const allCopies = lists.filter(e =>
            (
              normalizeClass(e.class).includes(normalizeClass(dup.class)) ||
              normalizeClass(dup.class).includes(normalizeClass(e.class))
            ) &&
            e.subject.toLowerCase().replace(/\s/g, '') === dup.subject.toLowerCase().replace(/\s/g, '')
          );
          for (const existing of allCopies) {
            await fetch(
              `${window.API_BASE_URL}/students/list?class=${encodeURIComponent(existing.class)}&section=${encodeURIComponent(existing.section)}&subject=${encodeURIComponent(existing.subject)}`,
              { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
            );
          }
        }
      }
      
      let totalImported = 0;
      let totalFailed = 0;
      const results = [];
      
      for (const list of parsedHtmlLists) {
        try {
          const convertedFile = await buildConvertedFile(list);
          const formDataToSend = new FormData();
          formDataToSend.append('file', convertedFile);
          
          const response = await fetch(`${window.API_BASE_URL}/students/import`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formDataToSend
          });
          
          if (response.ok) {
            const result = await response.json();
            const count = result.totalStudents || result.count || list.students.length;
            totalImported += count;
            results.push(`${list.metadata}: ${count} students`);
          } else {
            totalFailed++;
            try {
              const errorData = await response.json();
              console.error('Import error for', list.metadata, ':', errorData);
            } catch (_) {}
          }
        } catch (err) {
          totalFailed++;
          console.error('Import error for', list.metadata, ':', err);
        }
      }
      
      if (totalImported > 0) {
        setSuccess(`\u2705 Successfully imported ${totalImported} students from ${parsedHtmlLists.length - totalFailed} of ${parsedHtmlLists.length} list(s)`);
        
        // Reset form
        setFile(null);
        setFiles([]);
        setPreviewData(null);
        setNonStandardFile(false);
        setConvertedStudents([]);
        setManualMetadata({ class: '', section: '', subject: '', teacher: '' });
        setParsedHtmlLists([]);
        document.getElementById('file-input').value = '';
        
        await fetchLists();
      } else {
        setError('Failed to import any lists. Check console for details.');
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to import lists'));
    } finally {
      setLoading(false);
    }
  };
  
  const showConfirm = (text, callback) => setConfirm({ show: true, text, onConfirm: callback });
  const handleConfirm = () => { 
    const callback = confirm.onConfirm;
    setConfirm({ show: false, text: '', onConfirm: null });
    // Call callback after closing dialog to allow nested confirmations
    if (callback) {
      setTimeout(() => callback(), 100);
    }
  };
  const handleCancel = () => {
    const onCancel = confirm.onCancel;
    setConfirm({ show: false, text: '', onConfirm: null });
    if (onCancel) onCancel();
  };
  
  const continueImport = async () => {
    setLoading(true);
    try {

      // Check if list with same class/section/subject already exists
      // For non-standard files, use manual metadata for comparison
      const checkMetadata = nonStandardFile
        ? `${manualMetadata.class}-${manualMetadata.section} · ${manualMetadata.subject}`
        : (previewData && previewData.metadata ? previewData.metadata : '');
      
      if (checkMetadata) {
        // Find ALL existing copies with matching class/section/subject
        const existingCopies = lists.filter(list => {
          const metadataStr = checkMetadata.toLowerCase().replace(/[\s,·+-]/g, '');
          return metadataStr.includes(list.class.toLowerCase().replace(/[\s+-]/g, '')) &&
                 metadataStr.includes(list.section.toLowerCase()) &&
                 metadataStr.includes(list.subject.toLowerCase().replace(/\s/g, ''));
        });

        if (existingCopies.length > 0) {
          const first = existingCopies[0];
          await new Promise((resolve) => {
            showConfirm(
              `A student list for "${formatListLabel(first.class, first.subject)}" already exists${existingCopies.length > 1 ? ` (${existingCopies.length} copies)` : ''}.\n\nDo you want to replace ${existingCopies.length > 1 ? 'all copies' : 'it'} with the new list?\n\nNew list: ${previewData ? previewData.studentCount : '?'} students`,
              async () => { resolve(true); }
            );
          });
          
          // Delete ALL copies
          for (const existing of existingCopies) {
            const deleteResponse = await fetch(
              `${window.API_BASE_URL}/students/list?class=${encodeURIComponent(existing.class)}&section=${encodeURIComponent(existing.section)}&subject=${encodeURIComponent(existing.subject)}`,
              {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
              }
            );
            if (!deleteResponse.ok) {
              console.error('Failed to delete copy:', existing.class, existing.section, existing.subject);
            }
          }
        }
      }

      // If non-standard file, build a converted xlsx in memory
      const uploadFile = nonStandardFile ? await buildConvertedFile() : file;

      const formDataToSend = new FormData();
      formDataToSend.append('file', uploadFile);

      const response = await fetch(`${window.API_BASE_URL}/students/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (response.ok) {
        const result = await response.json();
        const countMsg = result.totalStudents ? result.totalStudents : result.count;
        const classMsg = result.classCount && result.classCount > 1 ? ` from ${result.classCount} classes` : '';
        setSuccess(`✅ Successfully imported ${countMsg} students${classMsg}`);
        
        // Reset form
        setFile(null);
        setPreviewData(null);
        setNonStandardFile(false);
        setConvertedStudents([]);
        setManualMetadata({ class: '', section: '', subject: '', teacher: '' });
        setParsedHtmlLists([]);
        document.getElementById('file-input').value = '';
        
        // Refresh lists
        await fetchLists();
      } else {
        const errorData = await response.json();
        throw new Error(getErrorMessage(errorData, 'Failed to import students'));
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Perform Done processing
  const performDoneProcessing = async () => {
    try {
      if (!selectedList || students.length === 0) {
        return { success: false, error: 'No students to process' };
      }

      const token = localStorage.getItem('authToken');
      if (!token) {
        return { success: false, error: 'No authentication token found' };
      }
      
      // Get today's date in DD-MM-YYYY format
      const today = new Date();
      const day = String(today.getDate()).padStart(2, '0');
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const year = today.getFullYear();
      const todayDate = `${day}-${month}-${year}`;
      
      let updateCount = 0;
      
      // Fill empty fields for all students
      for (const student of students) {
        const currentFirstName = student.first_name;
        const currentMarks = student.marks;
        const currentComments = student.comments;
        
        const firstName = (currentFirstName && currentFirstName.trim() !== '') ? currentFirstName : '.';
        const marks = (currentMarks && currentMarks !== '-' && currentMarks.trim() !== '') ? currentMarks : '0';
        const comments = (currentComments && currentComments !== '-' && currentComments.trim() !== '') ? currentComments : 'Absent';

        if (firstName !== currentFirstName || marks !== currentMarks || comments !== currentComments) {
          const updateData = {
            student_name: student.student_name,
            first_name: firstName,
            marks: marks,
            comments: comments
          };
          
          const response = await fetch(`${window.API_BASE_URL}/students/${student.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(updateData)
          });

          if (response.ok) {
            updateCount++;
          } else {
            const errorText = await response.text();
            return { success: false, error: `Failed to update student ${student.student_name}: ${errorText}` };
          }
        }
      }
      
      // Refresh the list to show updated data
      await fetchStudents(selectedList.class, selectedList.section, selectedList.subject);
      setDoneClicked(true);
      
      return { success: true, updateCount, date: todayDate };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Perform copy to clipboard
  const performCopy = async () => {
    try {
      if (!selectedList || students.length === 0) {
        return { success: false, error: 'No students to copy' };
      }

      // Copy ALL students (including absent ones)
      // Create tab-separated data: Marks, Comments, Points ('a' for absent).
      const data = students.map(s => {
        const marks = (s.marks && s.marks !== '-' && s.marks.trim() !== '') ? s.marks : '0';
        const comments = (s.comments && s.comments !== '-' && s.comments.trim() !== '') ? s.comments : 'Absent';
        const points = isStudentAbsent(s) ? 'a' : '';
        return `${marks}\t${comments}\t${points}`;
      }).join('\n');

      // Copy to clipboard (with fallback for insecure HTTP contexts)
      const ok = await copyToClipboard(data);
      if (!ok) {
        return { success: false, error: 'Failed to copy to clipboard' };
      }
      
      return { success: true, count: students.length };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const checkNameMismatch = (studentName, checkedName) => {
    if (!studentName || !checkedName) return false;
    
    // Extract names without roll numbers
    const extractName = (str) => {
      const withoutRoll = str.replace(/\([^)]+\)/g, '').trim();
      // Replace hyphens and periods with spaces, then remove all spaces for comparison
      return withoutRoll.toLowerCase().replace(/[-.]/g, ' ').replace(/\s+/g, '');
    };
    
    const name1 = extractName(studentName);
    const name2 = extractName(checkedName);
    
    // Check if names are significantly different (not just typos)
    return name1 !== name2 && !name1.includes(name2) && !name2.includes(name1);
  };

  const getSortedStudents = () => {
    if (!sortConfig.key) return students;
    
    const sorted = [...students].sort((a, b) => {
      let aVal = a[sortConfig.key] || '';
      let bVal = b[sortConfig.key] || '';
      
      // Handle numeric sorting for marks
      if (sortConfig.key === 'marks') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };

  // Get unique course (class+subject) options from lists
  const getCourseOptions = () => {
    const seen = new Set();
    const options = [];
    lists.forEach(list => {
      const key = `${list.class}|${list.subject}`;
      if (list.class && list.subject && !seen.has(key)) {
        seen.add(key);
        options.push({
          value: key,
          label: `${list.class} \u00b7 ${list.subject}`,
          classVal: list.class,
          subject: list.subject
        });
      }
    });
    return options.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
  };

  // Course multi-select, then the class strip, then one section of that class.
  // Cards sort by school order: class, single sections before combined, letter, subject.
  const getFilteredLists = () => {
    let result = lists;
    if (gradeFilter || sectionFilter || subjectFilter) {
      result = result.filter(list => itemMatchesClass(list, gradeFilter, sectionFilter, subjectFilter));
    }
    if (listCourseFilter && listCourseFilter.length > 0) {
      result = result.filter(list =>
        listCourseFilter.some(f => list.class === f.classVal && list.subject === f.subject)
      );
    }
    return result.slice().sort(compareStudentLists);
  };

  const handleViewList = (list) => {
    setSelectedList(list);
    setSelectedStudentIds([]);
    setDoneClicked(false); // Reset done button state when viewing new list
    setCopyClicked(false);
    setSaveClicked(false);
    setSortConfig({ key: null, direction: 'asc' }); // Reset sorting
    fetchStudents(list.class, list.section, list.subject);
  };

  const handleDeleteList = async (list) => {
    if (isReadOnly) return;
    showConfirm(`Delete student list for ${formatListLabel(list.class, list.subject)}?`, async () => {

    try {
      const response = await fetch(
        `${window.API_BASE_URL}/students/list?class=${encodeURIComponent(list.class)}&section=${encodeURIComponent(list.section)}&subject=${encodeURIComponent(list.subject)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        setSuccess('Student list deleted successfully');
        
        // If currently viewing this list, clear the view
        if (selectedList && 
            selectedList.class === list.class && 
            selectedList.section === list.section && 
            selectedList.subject === list.subject) {
          setSelectedList(null);
          setStudents([]);
        }
        
        await fetchLists();
      } else {
        throw new Error('Failed to delete list');
      }
    } catch (err) {

      setError('Failed to delete student list');
    }
    });
  };

  // Helper: unique key for a list card
  const cardKey = (list) => `${list.class}|${list.section}|${list.subject}`;

  const handleToggleCardSelection = (list) => {
    const key = cardKey(list);
    setSelectedCardKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleSelectAllVisibleCards = () => {
    const visibleKeys = getFilteredLists().map(cardKey);
    const allSelected = visibleKeys.every(k => selectedCardKeys.includes(k));
    if (allSelected) {
      setSelectedCardKeys(prev => prev.filter(k => !visibleKeys.includes(k)));
    } else {
      setSelectedCardKeys(prev => Array.from(new Set([...prev, ...visibleKeys])));
    }
  };

  const handleDeleteSelectedLists = () => {
    if (isReadOnly) return;
    const toDelete = lists.filter(l => selectedCardKeys.includes(cardKey(l)));
    if (toDelete.length === 0) return;
    showConfirm(
      `Delete ${toDelete.length} selected list(s)? This cannot be undone.`,
      async () => {
        setLoading(true);
        let deletedCount = 0;
        let failedCount = 0;
        for (const list of toDelete) {
          try {
            const response = await fetch(
              `${window.API_BASE_URL}/students/list?class=${encodeURIComponent(list.class)}&section=${encodeURIComponent(list.section)}&subject=${encodeURIComponent(list.subject)}`,
              { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) { deletedCount++; } else { failedCount++; }
          } catch { failedCount++; }
        }
        setSelectedCardKeys([]);
        setMessage({
          show: true,
          text: `Deleted ${deletedCount} list(s)${failedCount > 0 ? `. Failed: ${failedCount}` : ''}`,
          type: 'success'
        });
        await fetchLists();
        setLoading(false);
      }
    );
  };

  // "Naheed.Akhtar" and "Naheed Akhtar" compare equal. An email matches on
  // its local part the same way ("naheed.akhtar@gmail.com" → "naheedakhtar").
  const normTeacherKey = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  // EduPage address for this list's teacher. The extension only matches a
  // full username, and the list stores a display name, not that address.
  const emailForList = (list) => {
    if (!list) return '';
    const tokens = String(list.teacher_names || '')
      .split(/[,;|/]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    const direct = tokens.find((t) => t.includes('@'));
    if (direct) return direct;

    const keys = new Set(tokens.map(normTeacherKey).filter(Boolean));
    for (const account of teacherAccounts) {
      const names = [account.full_name, account.username, String(account.email || '').split('@')[0]];
      if (names.some((n) => keys.has(normTeacherKey(n))) && String(account.email || '').includes('@')) {
        return String(account.email).trim();
      }
    }

    const same = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
    const folder = (scansFolders || []).find((f) =>
      same(f.class, list.class) && same(f.subject, list.subject) && String(f.created_by || '').includes('@')
    );
    if (folder) return String(folder.created_by).trim();

    const created = String(list.created_by || '').trim();
    return created.includes('@') ? created : '';
  };

  // Hand the opened class to the Login Edupage extension. Course line opens
  // that class; the email is what the extension matches. The display name is
  // left off the payload — the extension would search it as an assignment.
  const loginToEduPageForList = () => {
    if (!selectedList) return;
    const course = formatListLabel(selectedList.class, selectedList.subject);
    const teacher = String(selectedList.teacher_names || '').split(',')[0].trim();
    const email = emailForList(selectedList);
    const lines = email ? [course, email] : [course, teacher].filter(Boolean);
    const text = lines.join('\n');
    if (!text.trim()) return;
    window.postMessage({ source: 'fastgrader-edu', type: 'EDU_LOGIN_CARD', text }, '*');
    if (!eduBridgeReady) {
      setMessage({
        show: true,
        text: 'Sent to Login Edupage — if nothing opens, install/update the extension and reload.',
        type: 'info'
      });
    }
  };

  const handleDeleteAllLists = () => {
    if (isReadOnly) return;
    // First confirmation
    showConfirm(
      `⚠️ DELETE ALL LISTS?\n\nThis action cannot be undone.\n${lists.length} lists will be permanently deleted.`, 
      () => {
        // Second confirmation
        showConfirm(
          'Are you absolutely sure?\n\nThis will delete all student records permanently.',
          async () => {
            // Actual deletion logic
            setLoading(true);
            try {
              let deletedCount = 0;
              let failedCount = 0;

              for (const list of lists) {
                try {
                  const response = await fetch(
                    `${window.API_BASE_URL}/students/list?class=${encodeURIComponent(list.class)}&section=${encodeURIComponent(list.section)}&subject=${encodeURIComponent(list.subject)}`,
                    {
                      method: 'DELETE',
                      headers: {
                        'Authorization': `Bearer ${token}`
                      }
                    }
                  );

                  if (response.ok) {
                    deletedCount++;
                  } else {
                    failedCount++;
                  }
                } catch (err) {
                  failedCount++;
                }
              }

              setMessage({ 
                show: true, 
                text: `Deleted ${deletedCount} student list(s)${failedCount > 0 ? `. Failed: ${failedCount}` : ''}`, 
                type: 'success' 
              });
              setSelectedList(null);
              setStudents([]);
              await fetchLists();
            } catch (err) {
              setMessage({ show: true, text: 'Failed to delete all lists', type: 'error' });
            } finally {
              setLoading(false);
            }
          }
        );
      }
    );
  };

  // Commit the inline edit currently in `editingCell` — PUT the new value
  // to the backend, then update local state on success. Treats whitespace-
  // only as an intentional clear (we don't second-guess the user). When
  // the value is unchanged, just exits edit mode without a request.
  const commitCellEdit = async () => {
    if (isReadOnly) return;
    if (!editingCell) return;
    const { studentId, field, value } = editingCell;
    const student = students.find(s => s.id === studentId);
    if (!student) { setEditingCell(null); return; }
    const newValue = (value == null ? '' : String(value));
    const oldValue = student[field] == null ? '' : String(student[field]);
    if (newValue === oldValue) { setEditingCell(null); return; }

    try {
      const response = await fetch(`${window.API_BASE_URL}/students/${studentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          student_name: field === 'student_name' ? newValue : (student.student_name || ''),
          first_name:   field === 'first_name'   ? newValue : (student.first_name   || ''),
          marks:        field === 'marks'        ? newValue : (student.marks        || ''),
          comments:     field === 'comments'     ? newValue : (student.comments     || '')
        })
      });
      if (response.ok) {
        setStudents(prev => prev.map(s => s.id === studentId ? { ...s, [field]: newValue } : s));
      } else {
        setError('Failed to update student');
      }
    } catch (err) {
      setError('Failed to update student: ' + err.message);
    } finally {
      setEditingCell(null);
    }
  };

  const handleCellKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitCellEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingCell(null);
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (isReadOnly) return;
    showConfirm('Delete this student?', async () => {

    try {
      const response = await fetch(`${window.API_BASE_URL}/students/${studentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccess('Student deleted successfully');
        setSelectedStudentIds(prev => prev.filter(id => id !== studentId));
        
        // Refresh current list
        if (selectedList) {
          fetchStudents(selectedList.class, selectedList.section, selectedList.subject);
        }
        await fetchLists();
      } else {
        throw new Error('Failed to delete student');
      }
    } catch (err) {

      setError('Failed to delete student');
    }
    });
  };

  const handleClearAllData = async () => {
    showConfirm('Clear data (First Name, Marks, Comments) for ALL students?', async () => {

    setLoading(true);
    setError('');

    try {
      let clearCount = 0;
      
      for (const student of students) {
        if (student.first_name || student.marks || student.comments) {
          const response = await fetch(`${window.API_BASE_URL}/students/${student.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              student_name: student.student_name,
              first_name: '',
              marks: '',
              comments: ''
            })
          });

          if (response.ok) {
            clearCount++;
          } else {
            throw new Error('Failed to clear student data');
          }
        }
      }

      setSuccess(`Cleared data for ${clearCount} student(s)`);
      // Refresh current list
      if (selectedList) {
        fetchStudents(selectedList.class, selectedList.section, selectedList.subject);
      }
    } catch (err) {
      setError('Failed to clear student data');
    } finally {
      setLoading(false);
    }
    });
  };

  const handleDeleteFault = async (faultId, faultIndex) => {
    if (isReadOnly) return;
    showConfirm('Delete this unmatched student?', async () => {

    try {
      const response = await fetch(`${window.API_BASE_URL}/students/unmatched/${faultId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccess('Unmatched student deleted');
        setFaultStudents(faultStudents.filter((_, idx) => idx !== faultIndex));
      } else {
        throw new Error('Failed to delete');
      }
    } catch (err) {
      setError('Failed to delete unmatched student');
    }
    });
  };

  const handleSaveAllToMistakes = async () => {
    if (isReadOnly) return;
    if (!selectedList || faultStudents.length === 0) {
      setError('No unmatched papers to save');
      return;
    }

    showConfirm(`Save all ${faultStudents.length} unmatched papers to Mistakes tab?`, async () => {

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Save all fault students to Mistakes tab as PERMANENT records
      for (const fault of faultStudents) {
        const response = await fetch(`${window.API_BASE_URL}/students/unmatched`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            class: selectedList.class,
            section: selectedList.section,
            subject: selectedList.subject,
            name: fault.student_name || fault.name,
            marks: fault.marks || '',
            comments: 'PERMANENT:' + (fault.comments || ''),
            teacher_name: (selectedList.teacher_names || '').split(',')[0].trim()
          })
        });

        if (!response.ok) {
          throw new Error('Failed to save unmatched paper');
        }
        
        // Delete the temporary unmatched record
        if (fault.id) {
          await fetch(`${window.API_BASE_URL}/students/unmatched/${fault.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
        }
      }

      setSuccess(`Successfully saved ${faultStudents.length} unmatched papers to Mistakes tab`);
      setFaultStudents([]);
    } catch (err) {
      setError('Failed to save unmatched papers: ' + err.message);
    } finally {
      setLoading(false);
    }
    });
    return;
  };

  // Add `offset` to the trailing number in a filename, preserving leading
  // zeros and extension. e.g. offsetImageFilename('Image_33221.jpg', 3) ->
  // 'Image_33224.jpg'. Returns null if no trailing number is found.
  const offsetImageFilename = (name, offset) => {
    const m = String(name).match(/^(.*?)(\d+)(\.[^.]+)?$/);
    if (!m) return null;
    const prefix = m[1];
    const numStr = m[2];
    const ext = m[3] || '';
    const nextNum = String(parseInt(numStr, 10) + offset).padStart(numStr.length, '0');
    return `${prefix}${nextNum}${ext}`;
  };

  const handleFaultPreviewImage = async (comments) => {
    console.log('[StudentPreview] comments:', comments);
    const text = String(comments || '');

    // Collect every filename explicitly named in comments, plus each one's
    // +1 back side. Do NOT walk arbitrary [N sheets] offsets from a single
    // anchor — /scans/by-filename is a global lookup, so fabricated names
    // can resolve to images in other folders (e.g. MCQ scans).
    const imgRegex = /\[([^\]]+\.(?:jpg|jpeg|png|gif))\]/gi;
    const matches = Array.from(text.matchAll(imgRegex));
    console.log('[StudentPreview] matches found:', matches.length, matches.map(m => m[1]));
    if (matches.length === 0) return;

    const seen = new Set();
    const names = [];
    const addName = (n) => {
      if (n && !seen.has(n)) {
        seen.add(n);
        names.push(n);
      }
    };
    for (const m of matches) {
      const front = m[1];
      addName(front);
      const back = offsetImageFilename(front, 1);
      if (back && back !== front) addName(back);
    }
    console.log('[StudentPreview] names to fetch:', names);
    if (names.length === 0) return;

    const clickedName = matches[0][1];
    setFaultPreviewLoading(clickedName);
    const apiBase = window.API_BASE_URL;
    // Resolve which rubric this fault paper's scan lives in. /scans/by-filename
    // is a GLOBAL lookup that can return an unrelated scan sharing the same
    // filename (e.g. Image_01886.jpg also exists in another rubric for a
    // different class/subject). Narrow to the subjective rubric matching the
    // currently-open student list's class + subject. scansFolders is already
    // sorted newest-first and MCQ folders are filtered out, so the first
    // match is the right one. Fall back to selectedFolder, then global.
    const norm = (s) => String(s || '').trim().toLowerCase();
    const matchingFolder = selectedList && Array.isArray(scansFolders)
      ? scansFolders.find(f =>
          norm(f.class) === norm(selectedList.class) &&
          norm(f.subject) === norm(selectedList.subject)
        )
      : null;
    const rubricId = (matchingFolder && matchingFolder.id) || selectedFolder;
    console.log('[StudentPreview] resolved rubricId:', rubricId,
                '(via', matchingFolder ? `match class=${selectedList && selectedList.class} subject=${selectedList && selectedList.subject}` : 'selectedFolder', ')');
    const fetchOne = async (n) => {
      try {
        const url = rubricId
          ? `${apiBase}/scans/${rubricId}/preview/${encodeURIComponent(n)}`
          : `${apiBase}/scans/by-filename/${encodeURIComponent(n)}`;
        const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
        console.log(`[StudentPreview] fetch ${n}: status=${response.status}`);
        if (!response.ok) return null;
        const data = await response.json();
        console.log(`[StudentPreview] body ${n}:`, data);
        if (data && data.success) return { url: data.downloadUrl, name: n };
      } catch (err) {
        console.log(`[StudentPreview] threw ${n}:`, err && err.message);
      }
      return null;
    };

    // Parallel fetch. Open modal as soon as the clicked image resolves,
    // then upgrade to the full gallery once the rest arrive.
    const promises = names.map(fetchOne);
    const clickedIdx = Math.max(0, names.indexOf(clickedName));

    try {
      const first = await promises[clickedIdx];
      if (first) {
        setFaultPreviewImage({ images: [first], index: 0 });
      }
    } finally {
      setFaultPreviewLoading(null);
    }

    Promise.all(promises).then((results) => {
      const images = results.filter(Boolean);
      console.log('[StudentPreview] backfill complete, total images:', images.length, images.map(i => i.name));
      if (images.length === 0) return;
      const finalIndex = Math.max(0, images.findIndex(img => img.name === clickedName));
      setFaultPreviewImage(prev => {
        console.log('[StudentPreview] upgrading modal from', prev && prev.images && prev.images.length, 'to', images.length);
        return prev ? { images, index: finalIndex } : prev;
      });
    });
  };

  const handleClearAllUnmatched = async () => {
    if (isReadOnly) return;
    if (faultStudents.length === 0) return;
    showConfirm(`Clear all ${faultStudents.length} unmatched papers?`, async () => {
      setLoading(true);
      try {
        for (const fault of faultStudents) {
          if (fault.id) {
            await fetch(`${window.API_BASE_URL}/students/unmatched/${fault.id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` }
            });
          }
        }
        setFaultStudents([]);
        setSuccess('All unmatched papers cleared');
      } catch (err) {
        setError('Failed to clear unmatched papers');
      } finally {
        setLoading(false);
      }
    });
  };

  const handleMatchAllUnmatched = async () => {
    if (isReadOnly) return;
    if (!selectedList || faultStudents.length === 0) {
      setError('No unmatched papers to match');
      return;
    }

    showConfirm(`Automatically match all ${faultStudents.length} unmatched papers? Students will be created if they don't exist.`, async () => {

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let matchedCount = 0;
      let noRollNumberCount = 0;

      // Debug: log first fault student structure
      if (faultStudents.length > 0) {
        console.log('First fault student:', faultStudents[0]);
      }

      for (const fault of faultStudents) {
        const faultName = fault.student_name || fault.name || '';
        console.log('Processing fault:', { faultName, fault });
        
        const rollMatch = faultName.match(/\(([^)]+)\)/);
        const rollNumber = rollMatch ? rollMatch[1].trim().toLowerCase() : null;

        if (!rollNumber) {
          noRollNumberCount++;
          console.log(`Skipping: no roll number in "${faultName}"`);
          continue;
        }

        // Try to find existing student with this roll number
        const targetStudent = students.find(s => {
          const sRollMatch = s.student_name.match(/\(([^)]+)\)/);
          return sRollMatch && sRollMatch[1].trim().toLowerCase() === rollNumber;
        });

        if (targetStudent) {
          // Update existing student - use the full checked name as first_name
          const checkedName = faultName;
          const response = await fetch(`${window.API_BASE_URL}/students/${targetStudent.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              student_name: targetStudent.student_name,
              first_name: checkedName,
              marks: fault.marks || null,
              comments: fault.comments || null
            })
          });

          if (response.ok) {
            matchedCount++;
            
            // Save to Mistakes tab BEFORE deleting (to keep historical record)
            // Mark as PERMANENT so it won't be deleted on next approval
            await fetch(`${window.API_BASE_URL}/students/unmatched`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                class: selectedList.class,
                section: selectedList.section,
                subject: selectedList.subject,
                name: checkedName,
                marks: fault.marks || '',
                comments: 'PERMANENT:' + (fault.comments || ''),
                teacher_name: (selectedList.teacher_names || '').split(',')[0].trim()
              })
            });
            
            // Delete the unmatched record from unmatched_students table
            if (fault.id) {
              console.log(`✅ Deleting unmatched record with ID: ${fault.id}`);
              const deleteResponse = await fetch(`${window.API_BASE_URL}/students/unmatched/${fault.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (deleteResponse.ok) {
                console.log(`✅ Successfully deleted unmatched record ${fault.id}`);
              } else {
                const errorText = await deleteResponse.text();
                console.error(`❌ Failed to delete unmatched record ${fault.id}:`, deleteResponse.status, errorText);
              }
            } else {
              console.error('❌ CRITICAL: Fault has no ID, cannot delete:', JSON.stringify(fault, null, 2));
            }
          }
        } else {
          // Don't create new students - only match existing ones
          console.warn(`No matching student found for roll number: ${rollNumber}`);
        }
      }

      if (matchedCount === 0) {
        setError(`No students matched. ${noRollNumberCount} papers without roll numbers were skipped. Please manually match each student to their roll number.`);
      } else {
        let message = `✅ Successfully matched ${matchedCount} student(s)`;
        if (noRollNumberCount > 0) {
          message += `. ${noRollNumberCount} papers without roll numbers were skipped.`;
        }
        setSuccess(message);
        
        // Refresh fault students list to remove matched records
        if (selectedList) {
          await fetchStudents(selectedList.class, selectedList.section, selectedList.subject);
        }
      }
      // Don't clear faultStudents here - let the refresh handle it
    } catch (err) {
      setError('Failed to match all unmatched: ' + err.message);
    } finally {
      setLoading(false);
    }
    });
    return;
  };

  // Quick Add: open modal to select lists
  const handleQuickAddClick = () => {
    if (!quickStudentName.trim()) {
      setError('Please enter a student name first');
      return;
    }
    setSelectedListsForAction([]);
    setModalListFilter('');
    setShowAddToListsModal(true);
  };

  // Quick Add: confirm adding student to selected lists.
  //
  // For each selected list we first probe `/students?class=…` to see
  // whether the student already exists there, and skip the add if so.
  // Match is tolerant of formatting differences:
  //   "(A01)Saher"  ==  "(A01) Saher"  ==  "Saher"
  // — `normalize` collapses whitespace + lowercases; `stripPrefix`
  // additionally drops a leading "(roll)" so a user typing the bare
  // name still hits an existing roll-prefixed record. Without this
  // gate the backend would happily insert a duplicate row, which then
  // shows up twice in the list and breaks downstream matching.
  //
  // Summary message reports both counts so the teacher knows exactly
  // what happened (added vs skipped) without having to open each list.
  const handleQuickAddConfirm = async () => {
    if (isReadOnly) return;
    if (selectedListsForAction.length === 0) {
      setError('Please select at least one list');
      return;
    }
    setQuickActionLoading(true);
    setError('');
    try {
      const studentName = quickStudentName.trim();
      const normalize = (s) => (s || '').trim().replace(/\s+/g, ' ').toLowerCase();
      const stripPrefix = (s) => normalize(s).replace(/^\(([^)]*)\)\s*/, '');
      const wantNorm = normalize(studentName);
      const wantStripped = stripPrefix(studentName);

      let addedCount = 0;
      let skippedCount = 0;
      const skippedLabels = [];

      for (const listKey of selectedListsForAction) {
        const [cls, sec, sub] = listKey.split('|');
        const matchingList = lists.find(l => l.class === cls && l.section === sec && l.subject === sub);
        const listLabel = `${cls}${sec ? '-' + sec : ''} ${sub}`;

        // 1. Duplicate check — fetch existing students of this list.
        let alreadyExists = false;
        try {
          const existingRes = await fetch(
            `${window.API_BASE_URL}/students?class=${encodeURIComponent(cls)}&section=${encodeURIComponent(sec)}&subject=${encodeURIComponent(sub)}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (existingRes.ok) {
            const existing = await existingRes.json();
            alreadyExists = (existing || []).some(s => {
              const sn = s.student_name || '';
              return normalize(sn) === wantNorm || stripPrefix(sn) === wantStripped;
            });
          }
        } catch (_) {
          // Probe failed — fall through to the add anyway so a transient
          // network blip doesn't block the whole batch.
        }
        if (alreadyExists) {
          skippedCount++;
          if (skippedLabels.length < 5) skippedLabels.push(listLabel);
          continue;
        }

        // 2. Add — student isn't in this list yet.
        const response = await fetch(`${window.API_BASE_URL}/students/add`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            class: cls,
            section: sec,
            subject: sub,
            student_name: studentName,
            first_name: null,
            marks: null,
            comments: null,
            teacher_names: matchingList ? matchingList.teacher_names : null
          })
        });
        if (response.ok) addedCount++;
      }

      const parts = [];
      if (addedCount > 0) parts.push(`added to ${addedCount} list(s)`);
      if (skippedCount > 0) {
        const tail = skippedLabels.length < skippedCount ? `, …` : '';
        parts.push(`skipped ${skippedCount} (already present: ${skippedLabels.join(', ')}${tail})`);
      }
      setSuccess(`"${studentName}" — ${parts.join('; ') || 'no action taken'}`);
      setShowAddToListsModal(false);
      setQuickStudentName('');
      setSelectedListsForAction([]);
      await fetchLists();
    } catch (err) {
      setError('Failed to add student: ' + err.message);
    } finally {
      setQuickActionLoading(false);
    }
  };

  // Quick Remove: open the list-selection modal — mirrors Quick Add. We do NOT
  // sweep every list upfront anymore; the teacher picks the classes, and we
  // look the student up + delete per SELECTED list on confirm (fast, and the
  // exact same shape as Add). A class prefix typed above pre-filters the
  // dialog so it's still useful.
  const handleQuickRemoveClick = () => {
    if (!quickStudentName.trim()) {
      setError('Please enter a student name first');
      return;
    }
    setSelectedListsForAction([]);
    setModalListFilter(quickClassPrefix.trim());
    setShowRemoveFromListsModal(true);
  };

  // Quick Remove: confirm removing the student from the selected lists. For
  // each selected list we look the student up by name (tolerant of roll-prefix
  // and whitespace differences — same matcher as Quick Add) and delete the
  // matching record. Lists where the student isn't present are reported as
  // "not found" rather than treated as an error.
  const handleQuickRemoveConfirm = async () => {
    if (isReadOnly) return;
    if (selectedListsForAction.length === 0) {
      setError('Please select at least one list');
      return;
    }
    setQuickActionLoading(true);
    setError('');
    try {
      const studentName = quickStudentName.trim();
      const normalize = (s) => (s || '').trim().replace(/\s+/g, ' ').toLowerCase();
      const stripPrefix = (s) => normalize(s).replace(/^\(([^)]*)\)\s*/, '');
      const wantNorm = normalize(studentName);
      const wantStripped = stripPrefix(studentName);

      let removedCount = 0;
      let notFoundCount = 0;
      const notFoundLabels = [];

      for (const listKey of selectedListsForAction) {
        const [cls, sec, sub] = listKey.split('|');
        const listLabel = `${cls}${sec ? '-' + sec : ''} ${sub}`;

        // 1. Find the student in this list.
        let studentId = null;
        try {
          const existingRes = await fetch(
            `${window.API_BASE_URL}/students?class=${encodeURIComponent(cls)}&section=${encodeURIComponent(sec)}&subject=${encodeURIComponent(sub)}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (existingRes.ok) {
            const existing = await existingRes.json();
            const match = (Array.isArray(existing) ? existing : []).find(s => {
              const sn = s.student_name || '';
              return normalize(sn) === wantNorm || (wantStripped && stripPrefix(sn) === wantStripped);
            });
            if (match) studentId = match.id;
          }
        } catch (_) {
          // Probe failed — treat as not found so a transient blip doesn't
          // silently look like a successful removal.
        }
        if (!studentId) {
          notFoundCount++;
          if (notFoundLabels.length < 5) notFoundLabels.push(listLabel);
          continue;
        }

        // 2. Delete the matched record.
        try {
          const response = await fetch(`${window.API_BASE_URL}/students/${studentId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) removedCount++;
        } catch (_) {
          // ignore individual failures, continue the batch
        }
      }

      const parts = [];
      if (removedCount > 0) parts.push(`removed from ${removedCount} list(s)`);
      if (notFoundCount > 0) {
        const tail = notFoundLabels.length < notFoundCount ? `, …` : '';
        parts.push(`not found in ${notFoundCount} (${notFoundLabels.join(', ')}${tail})`);
      }
      setSuccess(`"${studentName}" — ${parts.join('; ') || 'no action taken'}`);
      setShowRemoveFromListsModal(false);
      setQuickStudentName('');
      setQuickClassPrefix('');
      setSelectedListsForAction([]);
      await fetchLists();
    } catch (err) {
      setError('Failed to remove student: ' + err.message);
    } finally {
      setQuickActionLoading(false);
    }
  };

  // Toggle a list in the multi-select for quick add/remove
  const toggleListSelection = (listKey) => {
    setSelectedListsForAction(prev =>
      prev.includes(listKey) ? prev.filter(k => k !== listKey) : [...prev, listKey]
    );
  };

  const handleAddStudent = async () => {
    if (isReadOnly) return;
    if (!newStudent.student_name || !newStudent.student_name.trim()) {
      setError('Student name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${window.API_BASE_URL}/students/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          class: selectedList.class,
          section: selectedList.section,
          subject: selectedList.subject,
          student_name: newStudent.student_name,
          first_name: newStudent.first_name || null,
          marks: newStudent.marks || null,
          comments: newStudent.comments || null,
          teacher_names: selectedList.teacher_names || null
        })
      });

      if (response.ok) {
        setSuccess('Student added successfully');
        setShowAddModal(false);
        setNewStudent({ student_name: '', first_name: '', marks: '', comments: '' });
        // Refresh student list and lists sequentially
        await fetchStudents(selectedList.class, selectedList.section, selectedList.subject);
        await fetchLists();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add student');
      }
    } catch (err) {
      setError('Failed to add student: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendToUnmatched = async (student) => {
    if (isReadOnly) return;
    if (!student.first_name && !student.marks && !student.comments) {
      setError('No data to move to unmatched');
      return;
    }

    showConfirm(`Move ${student.first_name}'s data (marks and comments) to unmatched list?`, async () => {

    setLoading(true);
    setError('');

    try {
      // First, save to unmatched list
      const saveResponse = await fetch(`${window.API_BASE_URL}/students/save-to-unmatched`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          student_name: student.first_name || student.student_name,
          marks: student.marks || null,
          comments: student.comments || '',
          class: selectedList.class,
          section: selectedList.section,
          subject: selectedList.subject
        })
      });

      if (!saveResponse.ok) {
        console.error('Save response:', saveResponse.status);
        throw new Error('Failed to move to unmatched');
      }

      // Then clear student's data
      const updateResponse = await fetch(`${window.API_BASE_URL}/students/${student.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          student_name: student.student_name,
          first_name: '',
          marks: '',
          comments: ''
        })
      });

      if (updateResponse.ok) {
        setSuccess('Data moved to unmatched list successfully');
        // Refresh the list
        await fetchStudents(selectedList.class, selectedList.section, selectedList.subject);
      } else {
        const errorData = await updateResponse.json();
        throw new Error(errorData.error || 'Failed to clear student data');
      }
    } catch (err) {
      setError('Failed to move to unmatched: ' + err.message);
    } finally {
      setLoading(false);
    }
    });
    return;
  };

  const handleCorrectFault = async (faultIndex, targetRollNumber) => {
    if (isReadOnly) return;
    if (!targetRollNumber || !targetRollNumber.trim()) {
      setError('Please enter a roll number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const fault = faultStudents[faultIndex];
      if (!fault) return;

      // Find student with matching roll number (case insensitive)
      const targetStudent = students.find(s => {
        const rollMatch = s.student_name.match(/\(([^)]+)\)/);
        return rollMatch && rollMatch[1].trim().toLowerCase() === targetRollNumber.trim().toLowerCase();
      });

      if (!targetStudent) {
        setError(`No student found with roll number ${targetRollNumber}`);
        setLoading(false);
        return;
      }

      // **PRESERVE EXISTING DATA** - If target student has data, move it to unmatched first
      if (targetStudent.first_name || targetStudent.marks || targetStudent.comments) {
        await fetch(`${window.API_BASE_URL}/students/save-to-unmatched`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            class: selectedList.class,
            section: selectedList.section,
            subject: selectedList.subject,
            student_name: targetStudent.student_name,
            first_name: targetStudent.first_name || '',
            marks: targetStudent.marks || null,
            comments: targetStudent.comments || ''
          })
        });
      }

      // Use the checked name as first_name (preserve as-is, including any roll numbers in the checked name)
      const checkedName = fault.student_name || fault.name || '';
      
      // Update that student with the fault data
      const response = await fetch(`${window.API_BASE_URL}/students/${targetStudent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          student_name: targetStudent.student_name,
          first_name: checkedName,
          marks: fault.marks,
          comments: fault.comments
        })
      });

      if (response.ok) {
        // Save to Mistakes tab BEFORE deleting (to keep historical record)
        // Mark as PERMANENT so it won't be deleted on next approval
        await fetch(`${window.API_BASE_URL}/students/unmatched`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            class: selectedList.class,
            section: selectedList.section,
            subject: selectedList.subject,
            name: checkedName,
            marks: fault.marks || '',
            comments: 'PERMANENT:' + (fault.comments || ''),
            teacher_name: (selectedList.teacher_names || '').split(',')[0].trim()
          })
        });
        
        // Delete the unmatched student from unmatched_students table (they're now in permanent Mistakes)
        const unmatchedId = fault.id;
        if (unmatchedId) {
          console.log(`✅ Deleting unmatched record with ID: ${unmatchedId}`);
          const deleteResponse = await fetch(`${window.API_BASE_URL}/students/unmatched/${unmatchedId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (deleteResponse.ok) {
            console.log(`✅ Successfully deleted unmatched record ${unmatchedId}`);
          } else {
            const errorText = await deleteResponse.text();
            console.error(`❌ Failed to delete unmatched record ${unmatchedId}:`, deleteResponse.status, errorText);
          }
        } else {
          console.error('❌ CRITICAL: Fault has no ID, cannot delete:', JSON.stringify(fault, null, 2));
        }
        
        setSuccess(`Successfully matched with ${targetStudent.student_name}`);
        
        // Refresh lists to update UI with latest data from backend
        if (selectedList) {
          await fetchStudents(selectedList.class, selectedList.section, selectedList.subject);
        }
        
        // Clear correction input
        setFaultCorrections(prev => {
          const updated = { ...prev };
          delete updated[faultIndex];
          return updated;
        });
      } else {
        throw new Error('Failed to update student');
      }
    } catch (err) {

      setError('Failed to match student');
    } finally {
      setLoading(false);
    }
  };

  // handleSaveResults, handleConfirmSave and performSaveToFolder were
  // removed — the "save the checked list into a scans folder" step now
  // runs as the first stage of the AssessmentTab Approve flow.

  const handleCopyMarks = async () => {
    try {
      // Extract all marks values
      const marksData = students
        .map(student => student.marks || '-')
        .join('\n');
      
      const ok = await copyToClipboard(marksData);
      if (!ok) {
        setError('Failed to copy marks to clipboard');
        return;
      }
      setShowCopiedMessage(true);
      setSuccess('Marks copied to clipboard!');
      
      setTimeout(() => {
        setShowCopiedMessage(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy marks:', err);
      setError('Failed to copy marks to clipboard');
    }
  };

  const handleDone = async () => {
    if (isReadOnly) return;
    if (!selectedList) {
      console.error('No list selected');
      return;
    }

    console.log('Done button clicked for list:', selectedList);
    setDoneClicked(true);
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      let updateCount = 0;
      
      // Get today's date in DD-MM-YYYY format
      const today = new Date();
      const day = String(today.getDate()).padStart(2, '0');
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const year = today.getFullYear();
      const todayDate = `${day}-${month}-${year}`;
      
      console.log('Processing', students.length, 'students');
      console.log('Today\'s date:', todayDate);
      
      // Fill empty first_name with '.', empty marks with 0, and empty comments with date-absent for all students
      for (const student of students) {
        const currentFirstName = student.first_name;
        const currentMarks = student.marks;
        const currentComments = student.comments;
        
        const firstName = (currentFirstName && currentFirstName.trim() !== '') ? currentFirstName : '.';
        const marks = (currentMarks && currentMarks !== '-' && currentMarks.trim() !== '') ? currentMarks : '0';
        const comments = (currentComments && currentComments !== '-' && currentComments.trim() !== '') ? currentComments : 'Absent';







        if (firstName !== currentFirstName || marks !== currentMarks || comments !== currentComments) {
          const updateData = {
            student_name: student.student_name,
            first_name: firstName,
            marks: marks,
            comments: comments
          };
          
          console.log(`Updating student ${student.id}:`, updateData);
          
          const response = await fetch(`${window.API_BASE_URL}/students/${student.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(updateData)
          });

          if (response.ok) {
            updateCount++;
            console.log(`Successfully updated student ${student.id}`);
          } else {
            const errorText = await response.text();
            console.error(`Failed to update student ${student.id}:`, errorText);
            throw new Error(`Failed to update student ${student.student_name}: ${errorText}`);
          }
        }
      }
      
      console.log(`Total updates: ${updateCount}`);
      
      // Refresh the list to show updated data
      console.log('Refreshing student list...');
      await fetchStudents(selectedList.class, selectedList.section, selectedList.subject);

      // Fill the Sort-tab "Absent" step box for the folder(s) this list came
      // from. The Students tab doesn't know the folder, so the backend
      // resolves it by class+subject (rubrics whose snapshot is
      // 'copy_to_edupage' — i.e. approved, awaiting the post-approve steps).
      // Best-effort telemetry: a failed report never breaks the mark-absent.
      try {
        await fetch(`${window.API_BASE_URL}/analyze/auto-check/step`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            class: selectedList.class,
            subject: selectedList.subject,
            step: 'absent',
            status: 'done'
          })
        });
      } catch (stepErr) {
        console.warn('[StudentsTab] Absent step report failed (non-fatal):', stepErr);
      }

      const successMsg = `✅ Updated ${updateCount} student(s): Empty first names filled with ".", empty marks filled with "0", and empty comments filled with "Absent"`;
      console.log(successMsg);
      setSuccess(successMsg);
    } catch (err) {
      console.error('Error in handleDone:', err);
      setError('❌ Failed to fill empty fields: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Reverse of handleDone / "Mark Absent". Finds every student the Mark-Absent
  // flow placeholder-filled (first name ".", comment "Absent") and clears their
  // first name, marks, and comment back to empty — so a class marked absent by
  // mistake can be undone in one click. Only touches placeholder rows
  // (isAbsentMarked); genuinely-graded students are left alone.
  const handleUnmarkAbsent = async () => {
    if (isReadOnly) return;
    if (!selectedList) {
      console.error('No list selected');
      return;
    }

    const targets = students.filter(isAbsentMarked);
    if (targets.length === 0) {
      setError('');
      setSuccess('No absent-marked students to clear.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      let clearedCount = 0;
      for (const student of targets) {
        const response = await fetch(`${window.API_BASE_URL}/students/${student.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            student_name: student.student_name,
            first_name: '',
            marks: '',
            comments: ''
          })
        });

        if (response.ok) {
          clearedCount++;
        } else {
          const errorText = await response.text();
          throw new Error(`Failed to clear ${student.student_name}: ${errorText}`);
        }
      }

      // Refresh so the cleared rows show empty again.
      await fetchStudents(selectedList.class, selectedList.section, selectedList.subject);
      // Absent marking undone → re-arm the "1. Mark Absent" button.
      setDoneClicked(false);
      setSuccess(`✅ Cleared absent marking for ${clearedCount} student(s): first name, marks, and "Absent" comment reset to empty`);
    } catch (err) {
      console.error('Error in handleUnmarkAbsent:', err);
      setError('❌ Failed to clear absent marking: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    handleCopyResults();
  };

  const handleCopyResults = () => {
    if (isReadOnly) return;
    if (!selectedList || students.length === 0) return;

    // Filter only matched students
    const matchedStudents = students.filter(s => s.first_name);

    if (matchedStudents.length === 0) {
      setError('No matched students to copy');
      return;
    }

    // Create tab-separated data: Marks, Comments, and a Points column that
    // holds 'a' for an absent student (empty otherwise). Treat '-' as empty.
    const data = matchedStudents.map(s => {
      const marks = (s.marks && s.marks !== '-') ? s.marks : '.';
      const comments = (s.comments && s.comments !== '-') ? s.comments : 'Absent';
      const points = isStudentAbsent(s) ? 'a' : '';
      return `${marks}\t${comments}\t${points}`;
    }).join('\n');

    // Copy to clipboard (with fallback for insecure HTTP contexts)
    copyToClipboard(data).then(ok => {
      if (ok) {
        setShowCopiedMessage(true);
        setTimeout(() => setShowCopiedMessage(false), 2000);
        setCopyClicked(true); // Permanently fade the button — step 2 done
        window.postMessage({ type: 'FG_MARKS_TO_EDUPAGE', data }, '*');
        // The follow-up "click the 3rd button to mark Ready to Print"
        // reminder dialog was removed along with the 3rd button itself —
        // saving to a scans folder now happens at Approve time.

        // Marks are now in EduPage — flip any folder for this class+subject
        // that's awaiting the copy ('copy_to_edupage') to 'completed', so the
        // Sort-tab badge shows the batch is done. Best-effort; never blocks
        // the copy itself.
        (async () => {
          try {
            const token = localStorage.getItem('authToken');
            await fetch(`${window.API_BASE_URL}/analyze/snapshots/complete-by-class`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ class: selectedList.class, subject: selectedList.subject })
            });
          } catch (e) {
            console.warn('[StudentsTab] complete-by-class failed (non-fatal):', e);
          }
        })();
      } else {
        setError('Failed to copy to clipboard');
      }
    });
  };

  return (
    <div className="students-tab">
      {/* Two-column layout mirroring ScansTab: narrow LEFT rail holds the
          page header, Import / Quick-Add forms and the Available-Lists
          section header; the cards grid (or the selected-list table) fills
          the RIGHT side. Modals inside the wrapper are position:fixed and
          do not participate in flex layout. */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ width: 360, flexShrink: 0 }}>
      {/* Modern Header */}
      <div style={{
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          color: 'white',
          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
        }}>
          <i className="fa-solid fa-users"></i>
        </div>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.35rem', color: 'var(--text-primary)', fontWeight: '700' }}>Student Lists</h2>
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{isReadOnly ? 'View student records (read-only)' : 'Manage and view all student records'}</p>
        </div>
      </div>

      {/* Message Banner */}
      {messages.length > 0 && (
        <div className="messages-banner">
          <div className="banner-header">
            <span className="banner-icon"><i className="fa-solid fa-bullhorn"></i></span>
            <h4>Important Messages</h4>
          </div>
          <div className="banner-messages">
            {messages.slice(0, 3).map((msg) => (
              <div key={msg.id} className="banner-message">
                <div className="banner-message-text">{msg.message_text}</div>
                <div className="banner-message-meta">
                  <span className="banner-author">{msg.full_name || msg.username}</span>
                  <span className="banner-dot">·</span>
                  <span className="banner-date">{new Date(msg.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Add/Remove Student */}
      {!isReadOnly && (
      <div style={{
        background: 'var(--card-bg, white)',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid var(--border-color, #e0e0e0)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            color: 'white'
          }}>
            <i className="fa-solid fa-user-pen"></i>
          </div>
          <label style={{ fontSize: '0.875rem', margin: 0, fontWeight: '600', color: 'var(--text-primary)' }}>Quick Add / Remove Student</label>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Paste student name here..."
            value={quickStudentName}
            onChange={(e) => setQuickStudentName(e.target.value)}
            style={{
              flex: 1,
              minWidth: '200px',
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid var(--border-color, #d1d5db)',
              fontSize: '0.9rem',
              backgroundColor: 'var(--select-bg, white)',
              color: 'var(--text-primary)'
            }}
          />
          <input
            type="text"
            placeholder="Class prefix (e.g. 7)"
            value={quickClassPrefix}
            onChange={(e) => setQuickClassPrefix(e.target.value)}
            title="Optional: only search lists whose class starts with this. Speeds up Remove."
            style={{
              width: '170px',
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid var(--border-color, #d1d5db)',
              fontSize: '0.9rem',
              backgroundColor: 'var(--select-bg, white)',
              color: 'var(--text-primary)'
            }}
          />
          <button
            onClick={handleQuickAddClick}
            disabled={quickActionLoading || !quickStudentName.trim()}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: (!quickStudentName.trim() || quickActionLoading) ? '#ccc' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              fontSize: '0.85rem',
              fontWeight: '600',
              cursor: (!quickStudentName.trim() || quickActionLoading) ? 'not-allowed' : 'pointer',
              boxShadow: (!quickStudentName.trim() || quickActionLoading) ? 'none' : '0 2px 8px rgba(16, 185, 129, 0.3)',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap'
            }}
          >
            <i className="fa-solid fa-plus" style={{ marginRight: '6px' }}></i>Add Student
          </button>
          <button
            onClick={handleQuickRemoveClick}
            disabled={quickActionLoading || !quickStudentName.trim()}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: (!quickStudentName.trim() || quickActionLoading) ? '#ccc' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              fontSize: '0.85rem',
              fontWeight: '600',
              cursor: (!quickStudentName.trim() || quickActionLoading) ? 'not-allowed' : 'pointer',
              boxShadow: (!quickStudentName.trim() || quickActionLoading) ? 'none' : '0 2px 8px rgba(239, 68, 68, 0.3)',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap'
            }}
          >
            <i className="fa-solid fa-minus" style={{ marginRight: '6px' }}></i>Remove Student
          </button>
        </div>
      </div>
      )}

      {/* Import Form - Enhanced */}
      {!isReadOnly && (
      <div style={{
        background: 'var(--card-bg, white)',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid var(--border-color, #e0e0e0)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            color: 'white'
          }}>
            <i className="fa-solid fa-file-import"></i>
          </div>
<label className="import-label" style={{ fontSize: '0.875rem', margin: 0 }}>Import Student Lists (.xlsx, .xls, .html)</label>
        </div>

        <div className="import-inline-row" style={{ marginBottom: previewData ? '12px' : 0 }}>
          <input
            id="file-input"
            type="file"
            accept=".xlsx,.xls,.html,.htm"
            onChange={handleFileChange}
            className="import-file-input"
            multiple
            style={{ fontSize: '0.875rem' }}
          />
          <button 
            type="button" 
            className="btn-import-inline" 
            onClick={files.length > 1 ? handleImportMultipleFiles : handleSave}
            disabled={loading || !file}
            style={{
              background: (loading || !file) ? '#ccc' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              fontSize: '0.875rem',
              padding: '8px 20px',
              borderRadius: '8px',
              boxShadow: (loading || !file) ? 'none' : '0 2px 8px rgba(16, 185, 129, 0.3)'
            }}
          >
            {loading ? 'Importing...' : files.length > 1 ? `Import ${files.length} Files` : 'Save Students List'}
          </button>
        </div>
        
        {/* Preview extracted data */}
        {previewData && (
          <div style={{
            padding: '12px 16px',
            background: previewData.isNonStandard 
              ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' 
              : 'var(--preview-bg, linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%))',
            borderRadius: '8px',
            border: previewData.isNonStandard 
              ? '1px solid #f59e0b'
              : '1px solid var(--preview-border, #bfdbfe)',
            fontSize: '0.8rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <i className={previewData.isNonStandard ? "fa-solid fa-triangle-exclamation" : "fa-solid fa-chart-bar"} style={{ color: previewData.isNonStandard ? '#d97706' : 'var(--accent-color, #1e40af)' }}></i>
              <span style={{ fontWeight: '600', color: previewData.isNonStandard ? '#d97706' : 'var(--accent-color, #1e40af)' }}>
                {previewData.isNonStandard 
                  ? (previewData.multiList 
                    ? `${previewData.listCount} lists auto-detected — click Save to import all`
                    : (previewData.autoFilled ? 'Non-standard format — auto-detected details (verify below)' : 'Non-standard format detected — fill in details below'))
                  : `Preview (${previewData.totalFiles} file${previewData.totalFiles > 1 ? 's' : ''})`}
              </span>
            </div>
            {/* Standard or single non-standard preview */}
            {!previewData.multiList && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '0.75rem' }}>
                {(!previewData.isNonStandard || previewData.autoFilled) && <span className="preview-item"><strong>Class:</strong> {previewData.metadata}</span>}
                {(!previewData.isNonStandard || previewData.autoFilled) && <span className="preview-item"><strong>Teachers:</strong> {previewData.teacherNames}</span>}
                <span className="preview-item"><strong>Students found:</strong> {previewData.studentCount}</span>
                {previewData.totalFiles > 1 && <span className="preview-item" style={{ color: '#f59e0b', fontWeight: 'bold' }}>+ {previewData.totalFiles - 1} more</span>}
              </div>
            )}
            {/* Multi-list preview */}
            {previewData.multiList && parsedHtmlLists.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
                {parsedHtmlLists.map((list, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '6px 10px',
                    background: 'rgba(255,255,255,0.7)',
                    borderRadius: '6px',
                    fontSize: '0.75rem'
                  }}>
                    <span style={{ fontWeight: '700', color: '#d97706', minWidth: '18px' }}>{idx + 1}.</span>
                    <span><strong>{list.metadata}</strong></span>
                    <span style={{ color: 'var(--text-secondary)' }}>Teacher: {list.teacher || 'N/A'}</span>
                    <span style={{ color: '#059669', fontWeight: '600' }}>{list.students.length} students</span>
                  </div>
                ))}
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                  <strong>Total:</strong> {previewData.studentCount} students across {previewData.listCount} lists
                </div>
              </div>
            )}
          </div>
        )}

        {/* Manual metadata form for single non-standard files (not multi-list) */}
        {nonStandardFile && previewData && !previewData.multiList && (
          <div style={{
            padding: '14px 16px',
            marginTop: '12px',
            background: 'var(--card-bg, white)',
            borderRadius: '8px',
            border: '2px solid #f59e0b',
            fontSize: '0.8rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <i className="fa-solid fa-pen" style={{ color: '#d97706' }}></i>
              <span style={{ fontWeight: '600', color: '#d97706' }}>Enter class details for imported students</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', marginBottom: '4px', color: 'var(--text-secondary)' }}>Class *</label>
                <input
                  type="text"
                  placeholder="e.g. 5C"
                  value={manualMetadata.class}
                  onChange={(e) => setManualMetadata(prev => ({ ...prev, class: e.target.value }))}
                  style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border-color, #d1d5db)', fontSize: '0.8rem', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', marginBottom: '4px', color: 'var(--text-secondary)' }}>Section (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. C"
                  value={manualMetadata.section}
                  onChange={(e) => setManualMetadata(prev => ({ ...prev, section: e.target.value }))}
                  style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border-color, #d1d5db)', fontSize: '0.8rem', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', marginBottom: '4px', color: 'var(--text-secondary)' }}>Subject *</label>
                <input
                  type="text"
                  placeholder="e.g. COMPUTER"
                  value={manualMetadata.subject}
                  onChange={(e) => setManualMetadata(prev => ({ ...prev, subject: e.target.value }))}
                  style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border-color, #d1d5db)', fontSize: '0.8rem', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', marginBottom: '4px', color: 'var(--text-secondary)' }}>Teacher</label>
                <input
                  type="text"
                  placeholder="e.g. Abdullah.Subhani"
                  value={manualMetadata.teacher}
                  onChange={(e) => setManualMetadata(prev => ({ ...prev, teacher: e.target.value }))}
                  style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border-color, #d1d5db)', fontSize: '0.8rem', boxSizing: 'border-box' }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Add to Lists Modal */}
      {!isReadOnly && showAddToListsModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2000, padding: '20px'
        }} onClick={() => { setShowAddToListsModal(false); setSelectedListsForAction([]); }}>
          <div style={{
            background: 'var(--panel-bg, white)', borderRadius: '16px', padding: '24px',
            maxWidth: '550px', width: '100%', maxHeight: '95vh', overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: 'var(--text-primary)' }}>
                <i className="fa-solid fa-plus-circle" style={{ color: '#10b981', marginRight: '8px' }}></i>
                Add "{quickStudentName.trim()}" to Lists
              </h3>
              <button onClick={() => { setShowAddToListsModal(false); setSelectedListsForAction([]); }}
                style={{ background: '#EF4444', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem' }}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>
              Select the lists to add this student to:
            </p>
            <input
              type="text"
              placeholder="Type to filter lists (e.g. 5C, MATHS)..."
              value={modalListFilter}
              onChange={(e) => setModalListFilter(e.target.value)}
              autoFocus
              style={{
                width: '100%', padding: '8px 12px', borderRadius: '8px',
                border: '1px solid var(--border-color, #d1d5db)', fontSize: '0.85rem',
                marginBottom: '10px', boxSizing: 'border-box',
                backgroundColor: 'var(--select-bg, white)', color: 'var(--text-primary)'
              }}
            />
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px'
            }}>
              <button onClick={() => setSelectedListsForAction(lists.filter(l => { const label = formatListLabel(l.class, l.subject).toLowerCase(); return !modalListFilter || label.includes(modalListFilter.toLowerCase()); }).map(l => `${l.class}|${l.section}|${l.subject}`))}
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border-color, #ddd)', background: 'var(--surface, #f3f4f6)', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', color: 'var(--text-primary)' }}>
                Select All
              </button>
              <button onClick={() => setSelectedListsForAction([])}
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border-color, #ddd)', background: 'var(--surface, #f3f4f6)', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', color: 'var(--text-primary)' }}>
                Clear
              </button>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
                {selectedListsForAction.length} of {lists.length} selected
              </span>
            </div>
            <div style={{ maxHeight: '78vh', overflow: 'auto', border: '1px solid var(--border-color, #e0e0e0)', borderRadius: '8px' }}>
              {lists.filter(l => { const label = formatListLabel(l.class, l.subject).toLowerCase(); return !modalListFilter || label.includes(modalListFilter.toLowerCase()); }).map((list, idx, filteredArr) => {
                const key = `${list.class}|${list.section}|${list.subject}`;
                const isSelected = selectedListsForAction.includes(key);
                return (
                  <div key={idx} onClick={() => toggleListSelection(key)} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '3px 14px', cursor: 'pointer',
                    background: isSelected ? 'var(--select-blue-bg, #E0F2FE)' : 'var(--panel-bg, white)',
                    borderBottom: idx < filteredArr.length - 1 ? '1px solid var(--border-color, #f0f0f0)' : 'none',
                    transition: 'background 0.15s ease'
                  }}>
                    <input type="checkbox" checked={isSelected} readOnly style={{ cursor: 'pointer', accentColor: '#10b981' }} />
                    <span style={{ fontWeight: '600', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                      {formatListLabel(list.class, list.subject)}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
                      {list.student_count} students
                    </span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
              <button onClick={() => { setShowAddToListsModal(false); setSelectedListsForAction([]); }}
                style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid var(--border-color, #ddd)', background: 'var(--surface, #f3f4f6)', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', color: 'var(--text-primary)' }}>
                Cancel
              </button>
              <button onClick={handleQuickAddConfirm} disabled={quickActionLoading || selectedListsForAction.length === 0}
                style={{
                  padding: '10px 20px', borderRadius: '8px', border: 'none',
                  background: (quickActionLoading || selectedListsForAction.length === 0) ? '#ccc' : 'linear-gradient(135deg, #10b981, #059669)',
                  color: 'white', fontSize: '0.85rem', fontWeight: '600',
                  cursor: (quickActionLoading || selectedListsForAction.length === 0) ? 'not-allowed' : 'pointer',
                  boxShadow: (quickActionLoading || selectedListsForAction.length === 0) ? 'none' : '0 2px 8px rgba(16, 185, 129, 0.3)'
                }}>
                {quickActionLoading ? 'Adding...' : `Add to ${selectedListsForAction.length} List(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove from Lists Modal */}
      {!isReadOnly && showRemoveFromListsModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2000, padding: '20px'
        }} onClick={() => { setShowRemoveFromListsModal(false); setSelectedListsForAction([]); }}>
          <div style={{
            background: 'var(--panel-bg, white)', borderRadius: '16px', padding: '24px',
            maxWidth: '550px', width: '100%', maxHeight: '95vh', overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: 'var(--text-primary)' }}>
                <i className="fa-solid fa-minus-circle" style={{ color: '#ef4444', marginRight: '8px' }}></i>
                Remove "{quickStudentName.trim()}" from Lists
              </h3>
              <button onClick={() => { setShowRemoveFromListsModal(false); setSelectedListsForAction([]); }}
                style={{ background: '#EF4444', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem' }}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
              Select the lists to remove this student from:
            </p>
            <input
              type="text"
              placeholder="Type to filter lists (e.g. 5C, MATHS)..."
              value={modalListFilter}
              onChange={(e) => setModalListFilter(e.target.value)}
              autoFocus
              style={{
                width: '100%', padding: '8px 12px', borderRadius: '8px',
                border: '1px solid var(--border-color, #d1d5db)', fontSize: '0.85rem',
                marginBottom: '10px', boxSizing: 'border-box',
                backgroundColor: 'var(--select-bg, white)', color: 'var(--text-primary)'
              }}
            />
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px'
            }}>
              <button onClick={() => setSelectedListsForAction(lists.filter(l => { const label = formatListLabel(l.class, l.subject).toLowerCase(); return !modalListFilter || label.includes(modalListFilter.toLowerCase()); }).map(l => `${l.class}|${l.section}|${l.subject}`))}
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border-color, #ddd)', background: 'var(--surface, #f3f4f6)', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', color: 'var(--text-primary)' }}>
                Select All
              </button>
              <button onClick={() => setSelectedListsForAction([])}
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border-color, #ddd)', background: 'var(--surface, #f3f4f6)', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', color: 'var(--text-primary)' }}>
                Clear
              </button>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
                {selectedListsForAction.length} of {lists.length} selected
              </span>
            </div>
            <div style={{ maxHeight: '78vh', overflow: 'auto', border: '1px solid var(--border-color, #e0e0e0)', borderRadius: '8px' }}>
              {lists.filter(l => { const label = formatListLabel(l.class, l.subject).toLowerCase(); return !modalListFilter || label.includes(modalListFilter.toLowerCase()); }).map((list, idx, filteredArr) => {
                const key = `${list.class}|${list.section}|${list.subject}`;
                const isSelected = selectedListsForAction.includes(key);
                return (
                  <div key={idx} onClick={() => toggleListSelection(key)} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '3px 14px', cursor: 'pointer',
                    background: isSelected ? 'var(--select-warning-bg, #FEE2E2)' : 'var(--panel-bg, white)',
                    borderBottom: idx < filteredArr.length - 1 ? '1px solid var(--border-color, #f0f0f0)' : 'none',
                    transition: 'background 0.15s ease'
                  }}>
                    <input type="checkbox" checked={isSelected} readOnly style={{ cursor: 'pointer', accentColor: '#ef4444' }} />
                    <span style={{ fontWeight: '600', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                      {formatListLabel(list.class, list.subject)}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
                      {list.student_count} students
                    </span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
              <button onClick={() => { setShowRemoveFromListsModal(false); setSelectedListsForAction([]); }}
                style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid var(--border-color, #ddd)', background: 'var(--surface, #f3f4f6)', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', color: 'var(--text-primary)' }}>
                Cancel
              </button>
              <button onClick={handleQuickRemoveConfirm} disabled={quickActionLoading || selectedListsForAction.length === 0}
                style={{
                  padding: '10px 20px', borderRadius: '8px', border: 'none',
                  background: (quickActionLoading || selectedListsForAction.length === 0) ? '#ccc' : 'linear-gradient(135deg, #ef4444, #dc2626)',
                  color: 'white', fontSize: '0.85rem', fontWeight: '600',
                  cursor: (quickActionLoading || selectedListsForAction.length === 0) ? 'not-allowed' : 'pointer',
                  boxShadow: (quickActionLoading || selectedListsForAction.length === 0) ? 'none' : '0 2px 8px rgba(239, 68, 68, 0.3)'
                }}>
                {quickActionLoading ? 'Removing...' : `Remove from ${selectedListsForAction.length} List(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Automation Progress Modal */}
      {isAutomating && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '20px',
            maxWidth: '600px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            border: '3px solid #2196F3'
          }}>
            <h2 style={{ 
              margin: '0 0 30px 0', 
              fontSize: '24px',
              fontWeight: 'bold',
              textAlign: 'center',
              color: '#1976D2'
            }}>
              {automationStep === 'complete' ? (
                <span><i className="fa-solid fa-circle-check" style={{ marginRight: '8px' }}></i>All Done!</span>
              ) : (
                <span><i className="fa-solid fa-bolt" style={{ marginRight: '8px' }}></i>Processing Your Request</span>
              )}
            </h2>
            
            {/* Progress Steps */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Step 1: Saving */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                padding: '20px',
                background: '#F5F5F5',
                borderRadius: '12px',
                border: automationProgress.saving === 'inProgress' ? '3px solid #2196F3' : '3px solid #E0E0E0',
                transition: 'all 0.3s ease',
                transform: automationProgress.saving === 'inProgress' ? 'scale(1.02)' : 'scale(1)'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  background: automationProgress.saving === 'success' ? '#4CAF50' :
                             automationProgress.saving === 'error' ? '#f44336' :
                             automationProgress.saving === 'inProgress' ? '#2196F3' :
                             '#E0E0E0',
                  transition: 'all 0.3s ease',
                  boxShadow: automationProgress.saving === 'inProgress' ? '0 0 20px rgba(33, 150, 243, 0.5)' : 'none'
                }}>
                  {automationProgress.saving === 'success' ? '✓' :
                   automationProgress.saving === 'error' ? '✕' :
                   automationProgress.saving === 'inProgress' ? (
                     <div style={{
                       width: '20px',
                       height: '20px',
                       border: '3px solid rgba(33, 150, 243, 0.3)',
                       borderTop: '3px solid white',
                       borderRadius: '50%',
                       animation: 'spin 0.8s linear infinite'
                     }} />
                   ) : '1'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px', color: '#1976D2' }}>Save to Folder</div>
                  <div style={{ fontSize: '14px', opacity: 0.8, color: '#424242' }}>
                    {automationProgress.saving === 'success' ? 'Successfully saved to folder' :
                     automationProgress.saving === 'error' ? 'Failed to save to folder' :
                     automationProgress.saving === 'inProgress' ? 'Saving results to selected folder...' :
                     'Waiting to start...'}
                  </div>
                </div>
              </div>

              {/* Step 2: Processing */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                padding: '20px',
                background: '#F5F5F5',
                borderRadius: '12px',
                border: automationProgress.processing === 'inProgress' ? '3px solid #2196F3' : '3px solid #E0E0E0',
                transition: 'all 0.3s ease',
                transform: automationProgress.processing === 'inProgress' ? 'scale(1.02)' : 'scale(1)',
                opacity: automationProgress.saving === 'pending' ? 0.5 : 1
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  background: automationProgress.processing === 'success' ? '#4CAF50' :
                             automationProgress.processing === 'error' ? '#f44336' :
                             automationProgress.processing === 'inProgress' ? '#2196F3' :
                             '#E0E0E0',
                  transition: 'all 0.3s ease',
                  boxShadow: automationProgress.processing === 'inProgress' ? '0 0 20px rgba(33, 150, 243, 0.5)' : 'none'
                }}>
                  {automationProgress.processing === 'success' ? '✓' :
                   automationProgress.processing === 'error' ? '✕' :
                   automationProgress.processing === 'inProgress' ? (
                     <div style={{
                       width: '20px',
                       height: '20px',
                       border: '3px solid rgba(33, 150, 243, 0.3)',
                       borderTop: '3px solid white',
                       borderRadius: '50%',
                       animation: 'spin 0.8s linear infinite'
                     }} />
                   ) : '2'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px', color: '#1976D2' }}>Process Data (Done)</div>
                  <div style={{ fontSize: '14px', opacity: 0.8, color: '#424242' }}>
                    {automationProgress.processing === 'success' ? (
                      automationProgress.copying === 'pending' && countdown > 0 ? (
                        <span>All fields processed • Copying in <strong style={{ color: '#2196F3', fontSize: '16px' }}>{countdown}</strong> seconds...</span>
                      ) : 'All fields processed and filled'
                    ) :
                     automationProgress.processing === 'error' ? 'Failed to process student data' :
                     automationProgress.processing === 'inProgress' ? 'Filling empty fields, validating data...' :
                     'Waiting for save to complete...'}
                  </div>
                </div>
              </div>

              {/* Step 3: Copying */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                padding: '20px',
                background: '#F5F5F5',
                borderRadius: '12px',
                border: automationProgress.copying === 'inProgress' ? '3px solid #2196F3' : '3px solid #E0E0E0',
                transition: 'all 0.3s ease',
                transform: automationProgress.copying === 'inProgress' ? 'scale(1.02)' : 'scale(1)',
                opacity: automationProgress.processing === 'pending' ? 0.5 : 1
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  background: automationProgress.copying === 'success' ? '#4CAF50' :
                             automationProgress.copying === 'error' ? '#f44336' :
                             automationProgress.copying === 'inProgress' ? '#2196F3' :
                             '#E0E0E0',
                  transition: 'all 0.3s ease',
                  boxShadow: automationProgress.copying === 'inProgress' ? '0 0 20px rgba(33, 150, 243, 0.5)' : 'none'
                }}>
                  {automationProgress.copying === 'success' ? '✓' :
                   automationProgress.copying === 'error' ? '✕' :
                   automationProgress.copying === 'inProgress' ? (
                     <div style={{
                       width: '20px',
                       height: '20px',
                       border: '3px solid rgba(33, 150, 243, 0.3)',
                       borderTop: '3px solid white',
                       borderRadius: '50%',
                       animation: 'spin 0.8s linear infinite'
                     }} />
                   ) : '3'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px', color: '#1976D2' }}>Copy Results</div>
                  <div style={{ fontSize: '14px', opacity: 0.8, color: '#424242' }}>
                    {automationProgress.copying === 'success' ? 'Results copied to clipboard!' :
                     automationProgress.copying === 'error' ? 'Failed to copy results' :
                     automationProgress.copying === 'inProgress' ? 'Copying results to clipboard...' :
                     'Waiting for processing to complete...'}
                  </div>
                </div>
              </div>
            </div>

            {/* Completion Message */}
            {automationStep === 'complete' && (
              <div style={{
                marginTop: '30px',
                padding: '20px',
                background: '#E8F5E9',
                borderRadius: '12px',
                textAlign: 'center',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#2E7D32',
                border: '2px solid #4CAF50'
              }}>
                <i className="fa-solid fa-sparkles" style={{ marginRight: '8px' }}></i>All steps completed successfully! This window will close automatically.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Messages */}
      {error && <div className="message error-message">{error}</div>}
      {success && <div className="message success-message">{success}</div>}

      {/* Student Lists header + course filter — first half of the
          previously-monolithic lists-section. Splits in two so the header
          + chips can sit in the LEFT rail while the cards grid takes the
          RIGHT pane. */}
      {!selectedList && (
        <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              color: 'white'
            }}>
              <i className="fa-solid fa-book"></i>
            </div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
              Available Lists ({(gradeFilter || sectionFilter || subjectFilter || listCourseFilter.length > 0) ? getFilteredLists().length : lists.length})
              {(gradeFilter || sectionFilter || subjectFilter || listCourseFilter.length > 0) && <span style={{ fontSize: '0.8rem', fontWeight: '400', color: 'var(--text-secondary)', marginLeft: '8px' }}>of {lists.length}</span>}
            </h3>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {/* Select All visible cards */}
            {!isReadOnly && getFilteredLists().length > 0 && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600', color: 'var(--text-primary)', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={getFilteredLists().length > 0 && getFilteredLists().every(l => selectedCardKeys.includes(cardKey(l)))}
                  onChange={handleSelectAllVisibleCards}
                  style={{ cursor: 'pointer', accentColor: '#3b82f6', width: '15px', height: '15px' }}
                />
                Select All
              </label>
            )}
            {/* Bulk Delete button */}
            {!isReadOnly && selectedCardKeys.length > 0 && (
              <button
                onClick={handleDeleteSelectedLists}
                disabled={loading}
                style={{
                  padding: '8px 14px',
                  fontWeight: '600',
                  fontSize: '0.85rem',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  boxShadow: '0 2px 8px rgba(239,68,68,0.3)',
                  color: 'white',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <i className="fa-solid fa-trash"></i> Delete Selected ({selectedCardKeys.length})
              </button>
            )}
            {/* Multi-select Course Filter */}
            <div style={{ minWidth: 360, flex: '1 1 360px', maxWidth: 520 }}>
              <Select
                value={listCourseFilter}
                onChange={(selected) => { setListCourseFilter(selected || []); setSelectedCardKeys([]); }}
                inputValue={courseFilterInput}
                onInputChange={(val, action) => {
                  // Only update on user typing or menu-close; preserve text on option select
                  if (action.action === 'input-change') {
                    setCourseFilterInput(val);
                  }
                }}
                options={getCourseOptions()}
                isMulti
                isSearchable
                isClearable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                noOptionsMessage={() => 'No courses found'}
                formatOptionLabel={(option, { context }) => {
                  if (context === 'value') return <span>{option.label}</span>;
                  const isChecked = listCourseFilter.some(f => f.value === option.value);
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        readOnly
                        style={{ cursor: 'pointer', accentColor: '#3b82f6', width: '16px', height: '16px', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: '0.9rem' }}>{option.label}</span>
                    </div>
                  );
                }}
                controlShouldRenderValue={false}
                components={{
                  MenuList: (menuProps) => {
                    // Gather only the options currently rendered (i.e. matching the search filter)
                    const visibleOptions = React.Children.toArray(menuProps.children)
                      .map((child) => (child && child.props && child.props.data) ? child.props.data : null)
                      .filter(Boolean);
                    const currentValue = Array.isArray(listCourseFilter) ? listCourseFilter : [];
                    const allVisibleSelected = visibleOptions.length > 0 &&
                      visibleOptions.every(opt => currentValue.some(v => v.value === opt.value));
                    const handleToggleAllVisible = (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (visibleOptions.length === 0) return;
                      if (allVisibleSelected) {
                        // Deselect only the visible ones
                        const visibleValues = new Set(visibleOptions.map(o => o.value));
                        const next = currentValue.filter(v => !visibleValues.has(v.value));
                        setListCourseFilter(next);
                        setSelectedCardKeys([]);
                      } else {
                        // Add any visible that aren't already selected
                        const merged = [...currentValue];
                        visibleOptions.forEach(opt => {
                          if (!merged.some(v => v.value === opt.value)) merged.push(opt);
                        });
                        setListCourseFilter(merged);
                        setSelectedCardKeys([]);
                      }
                    };
                    return (
                      <selectComponents.MenuList {...menuProps}>
                        {visibleOptions.length > 0 && (
                          <div
                            onMouseDown={handleToggleAllVisible}
                            onClick={handleToggleAllVisible}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '10px 12px',
                              cursor: 'pointer',
                              background: 'var(--surface, #f0f4ff)',
                              borderBottom: '1px solid var(--border-color, #e5e7eb)',
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              color: 'var(--select-all-text, #1e40af)',
                              position: 'sticky',
                              top: 0,
                              zIndex: 1,
                              userSelect: 'none'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={allVisibleSelected}
                              readOnly
                              style={{ cursor: 'pointer', accentColor: '#3b82f6', width: '16px', height: '16px', flexShrink: 0 }}
                            />
                            <span>
                              {allVisibleSelected ? 'Deselect All' : 'Select All'}
                              {courseFilterInput ? ` matching "${courseFilterInput}" (${visibleOptions.length})` : ` (${visibleOptions.length})`}
                            </span>
                          </div>
                        )}
                        {menuProps.children}
                      </selectComponents.MenuList>
                    );
                  }
                }}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: 10,
                    borderColor: listCourseFilter.length > 0 ? '#3b82f6' : 'var(--border-color, #ddd)',
                    background: 'var(--panel-bg, white)',
                    color: 'var(--text-primary)',
                    fontSize: '0.9rem',
                    minHeight: 46,
                    minWidth: 360,
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 10000,
                    minWidth: 360,
                    background: 'var(--panel-bg, white)',
                    border: '1px solid var(--border-color, #ddd)'
                  }),
                  menuList: (base) => ({ ...base, maxHeight: 620, background: 'var(--panel-bg, white)' }),
                  option: (base, state) => ({
                    ...base,
                    background: state.isFocused ? 'var(--surface-hover, #f0f4ff)' : 'var(--panel-bg, white)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    padding: '3px 12px'
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: listCourseFilter.length > 0 ? '#3b82f6' : 'var(--text-muted, #888)'
                  }),
                  // The text the user types into the search box, plus the
                  // currently-shown value — both need theme-aware colour
                  // otherwise react-select's default near-black is invisible
                  // on the dark control background we just set.
                  input: (base) => ({ ...base, color: 'var(--text-primary)' }),
                  singleValue: (base) => ({ ...base, color: 'var(--text-primary)' }),
                  multiValue: (base) => ({
                    ...base,
                    background: 'var(--chip-bg, #dbeafe)',
                    color: 'var(--chip-text, #1e40af)'
                  }),
                  multiValueLabel: (base) => ({ ...base, color: 'var(--chip-text, #1e40af)' }),
                  noOptionsMessage: (base) => ({ ...base, color: 'var(--text-muted, #888)' })
                }}
                placeholder={listCourseFilter.length > 0 ? `${listCourseFilter.length} course(s) selected` : 'Search or select courses...'}
              />
            </div>
            {!isReadOnly && lists.length > 0 && (
              <button 
                className="btn-delete" 
                onClick={handleDeleteAllLists}
                disabled={loading}
                title="Delete all student lists"
                style={{ 
                  padding: '8px 16px', 
                  fontWeight: 'bold', 
                  fontSize: '0.85rem',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                  color: 'white',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                Delete All Lists
              </button>
            )}
          </div>
        </div>
        {/* Selected course chips - shown below the filter bar */}
        {listCourseFilter.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
            {listCourseFilter.map((course) => (
              <span
                key={course.value}
                style={{
                  background: 'var(--course-chip-bg, linear-gradient(135deg, #eff6ff, #dbeafe))',
                  color: 'var(--course-chip-text, #1e40af)',
                  padding: '5px 10px',
                  borderRadius: '8px',
                  fontSize: '0.82rem',
                  fontWeight: '600',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  border: '1px solid var(--course-chip-border, #bfdbfe)'
                }}
              >
                {course.label}
                <button
                  onClick={() => {
                    setListCourseFilter(prev => prev.filter(f => f.value !== course.value));
                    setSelectedCardKeys([]);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--course-chip-text, #1e40af)',
                    cursor: 'pointer',
                    padding: '0 2px',
                    fontSize: '14px',
                    fontWeight: '700',
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title="Remove filter"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </span>
            ))}
            <button
              onClick={() => { setListCourseFilter([]); setSelectedCardKeys([]); }}
              style={{
                background: 'none',
                border: '1px solid #ddd',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '0.78rem',
                fontWeight: '600'
              }}
            >
              Clear All
            </button>
          </div>
        )}
        </>
      )}
        </div>{/* close LEFT rail */}

        <div style={{ flex: 1, minWidth: 0 }}>
      {!selectedList && (
        <ClassSectionFilters
          items={lists}
          itemNoun="lists"
          gradeFilter={gradeFilter}
          sectionFilter={sectionFilter}
          onGrade={(grade) => {
            setSelectedCardKeys([]);
            setSubjectFilter(null);
            if (gradeFilter === grade) {
              setGradeFilter(null);
              setSectionFilter(null);
            } else {
              setGradeFilter(grade);
              setSectionFilter(null);
            }
          }}
          onSection={(section) => {
            setSelectedCardKeys([]);
            const next = sectionFilter === section ? null : section;
            setSectionFilter(next);
            if (subjectFilter && !itemsInClass(lists, gradeFilter, next).some(list => (list.subject || '').trim() === subjectFilter)) {
              setSubjectFilter(null);
            }
          }}
        />
      )}
      {!selectedList && (
        <>
        {lists.length === 0 ? (
          <div style={{
            background: 'var(--card-bg, white)',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center',
            border: '2px dashed var(--border-color, #e0e0e0)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.5 }}><i className="fa-solid fa-clipboard-list"></i></div>
            <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-muted)' }}>{isReadOnly ? 'No student lists assigned to you yet.' : 'No student lists found. Import one above.'}</p>
          </div>
        ) : getFilteredLists().length === 0 ? (
          <div style={{
            background: 'var(--card-bg, white)',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center',
            border: '2px dashed var(--border-color, #e0e0e0)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.5 }}><i className="fa-solid fa-filter-circle-xmark"></i></div>
            <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-muted)' }}>
              {(gradeFilter || sectionFilter || subjectFilter) ? 'No lists for this class.' : 'No lists found for the selected course(s).'}
            </p>
            <button
              onClick={() => { setListCourseFilter([]); setGradeFilter(null); setSectionFilter(null); setSubjectFilter(null); setSelectedCardKeys([]); }}
              style={{
                marginTop: '12px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Show All Lists
            </button>
          </div>
        ) : (
          <div className="lists-grid">
            {getFilteredLists().map((list, index) => {
              const isCardSelected = selectedCardKeys.includes(cardKey(list));
              return (
              <div key={list.id || `${list.class}-${list.section}-${list.subject}-${list.created_at}-${index}`} style={{
                background: isCardSelected ? 'var(--select-blue-bg, #EFF6FF)' : 'var(--card-bg, white)',
                borderRadius: '12px',
                padding: '16px',
                border: isCardSelected ? '2px solid #3b82f6' : '1px solid var(--border-color, #e0e0e0)',
                boxShadow: isCardSelected ? '0 2px 12px rgba(59,130,246,0.2)' : '0 2px 8px rgba(0,0,0,0.05)',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                if (!isCardSelected) {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isCardSelected) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                }
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px',
                  paddingBottom: '12px',
                  borderBottom: '2px solid var(--border-light, #f3f4f6)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {!isReadOnly && (
                    <input
                      type="checkbox"
                      checked={isCardSelected}
                      onChange={() => handleToggleCardSelection(list)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer', accentColor: '#3b82f6', width: '15px', height: '15px', flexShrink: 0 }}
                    />
                    )}
                    <h4 style={{
                      margin: 0,
                      fontSize: '0.95rem',
                      fontWeight: '700',
                      color: 'var(--text-primary)',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      {formatListLabel(list.class, list.subject)}
                    </h4>
                  </div>
                  <span style={{
                    background: 'var(--badge-bg, linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%))',
                    color: 'var(--badge-text, #1e40af)',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    border: '1px solid var(--badge-border, #bfdbfe)'
                  }}>{list.student_count} students</span>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <p style={{ margin: '6px 0', fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <i className="fa-solid fa-chalkboard-user"></i><strong style={{ color: 'var(--text-primary)', marginRight: '4px' }}>Teachers:</strong> {list.teacher_names}
                  </p>
                  <p style={{ margin: '6px 0', fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <i className="fa-solid fa-calendar"></i><strong style={{ color: 'var(--text-primary)', marginRight: '4px' }}>Checked:</strong> {formatCheckedDate(list.checked_date, list)} at {formatCheckedTime(list.checked_time)}
                  </p>
                  <p style={{ margin: '6px 0', fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <i className="fa-solid fa-user"></i><strong style={{ color: 'var(--text-primary)', marginRight: '4px' }}>Created by:</strong> {list.created_by}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    onClick={() => handleViewList(list)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: 'none',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      color: 'white',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    View Students
                  </button>
                  {!isReadOnly && (
                  <button 
                    onClick={() => handleDeleteList(list)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: 'none',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: 'white',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    Delete List
                  </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
        </>
      )}

      {/* Student Table - Enhanced */}
      {selectedList && (
        <div style={{
          background: 'var(--card-bg, white)',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid var(--border-color, #e0e0e0)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{ marginBottom: '12px' }}>
            <button
              onClick={() => {
                setSelectedList(null);
                setShowHistory(false);
                setSelectedHistoryBatch(null);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                background: 'transparent',
                color: 'var(--text-primary, #374151)',
                border: '1px solid var(--border-color, #d1d5db)',
                borderRadius: '8px',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              title="Back to all lists"
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(139, 92, 246, 0.08)'; e.currentTarget.style.borderColor = '#8b5cf6'; e.currentTarget.style.color = '#7c3aed'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'var(--border-color, #d1d5db)'; e.currentTarget.style.color = 'var(--text-primary, #374151)'; }}
            >
              <i className="fa-solid fa-arrow-left"></i> Back to All Lists
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
            {/* LEFT: the class that is actually open, with Login for its teacher */}
            <div style={{ flex: '0 0 auto', minWidth: '120px' }}>
              <div style={{
                display: 'inline-flex',
                flexDirection: 'column',
                background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(5,150,105,0.05))',
                border: '1px solid rgba(16,185,129,0.3)',
                borderRadius: '8px',
                padding: '6px 12px'
              }}>
                <span style={{ fontSize: '1rem', fontWeight: '700', color: '#059669' }}>
                  {formatListLabel(selectedList.class, selectedList.subject)}
                </span>
                {selectedList.teacher_names && (
                  <span style={{ fontSize: '0.8rem', color: '#10b981', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px', marginTop: 2 }}>
                    <i className="fa-solid fa-chalkboard-user"></i>{selectedList.teacher_names}
                  </span>
                )}
                <button
                  type="button"
                  onClick={loginToEduPageForList}
                  title={(() => {
                    const who = emailForList(selectedList) || String(selectedList.teacher_names || '').split(',')[0].trim() || 'this class';
                    return eduBridgeReady
                      ? `Log into EduPage as ${who} and open this class`
                      : `Log into EduPage as ${who} — needs the Login Edupage extension installed & updated`;
                  })()}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    marginTop: 4,
                    padding: '3px 8px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    border: '1px solid #059669',
                    borderRadius: 6,
                    background: 'transparent',
                    color: '#059669',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    alignSelf: 'flex-start'
                  }}
                >
                  <i className="fa-solid fa-right-to-bracket"></i>
                  Login
                </button>
              </div>
            </div>

            {/* CENTER: Course name + teacher */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1 1 auto', justifyContent: 'center' }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                color: 'white',
                flexShrink: 0
              }}>
                <i className="fa-solid fa-chart-line"></i>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>
                  {formatListLabel(selectedList.class, selectedList.subject)}
                </h3>
                {selectedList.teacher_names && (
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#10b981', fontWeight: '500' }}><i className="fa-solid fa-chalkboard-user" style={{ marginRight: '6px' }}></i>{selectedList.teacher_names}</p>
                )}
              </div>
            </div>

            {/* RIGHT: Action buttons */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', flex: '0 0 auto', justifyContent: 'flex-end' }}>
              <button
                onClick={openListHistory}
                style={{
                  padding: '8px 16px',
                  background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(13, 148, 136, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                title="Other assignments for this list"
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                <i className="fa-solid fa-clock-rotate-left"></i> Other assignments
              </button>
              {!isReadOnly && (
              <button
                onClick={handleDeleteSelectedStudents}
                disabled={selectedStudentIds.length === 0}
                style={{
                  padding: '8px 12px',
                  background: selectedStudentIds.length === 0 ? '#ccc' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  cursor: selectedStudentIds.length === 0 ? 'not-allowed' : 'pointer',
                  boxShadow: selectedStudentIds.length === 0 ? 'none' : '0 2px 6px rgba(239, 68, 68, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                title="Delete selected students"
              >
                <i className="fa-solid fa-trash"></i> Delete Selected ({selectedStudentIds.length})
              </button>
              )}
              {/* Refresh button - hidden */}
              {/*
              <button
                onClick={() => fetchStudents(selectedList.class, selectedList.section, selectedList.subject)}
                style={{
                  padding: '8px 12px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(59, 130, 246, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                title="Refresh"
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <i className="fa-solid fa-refresh"></i>
              </button>
              */}
              {/* Clear All Data button - hidden */}
              {/*
              <button
                onClick={handleClearAllData}
                style={{
                  padding: '8px 12px',
                  background: (loading || !students.some(s => s.first_name || s.marks || s.comments))
                    ? '#ccc'
                    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  cursor: (loading || !students.some(s => s.first_name || s.marks || s.comments)) ? 'not-allowed' : 'pointer',
                  boxShadow: (loading || !students.some(s => s.first_name || s.marks || s.comments)) ? 'none' : '0 2px 6px rgba(245, 158, 11, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                disabled={loading || !students.some(s => s.first_name || s.marks || s.comments)}
                title="Clear All Data"
                onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <i className="fa-solid fa-eraser"></i>
              </button>
              */}
              {/* Add Student button */}
              {!isReadOnly && (
              <button 
                onClick={() => setShowAddModal(true)}
                style={{ 
                  padding: '8px 12px', 
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(139, 92, 246, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                title="Add Student"
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <i className="fa-solid fa-plus"></i>
              </button>
              )}
              {/* Done button */}
              {!isReadOnly && (
              <button
                onClick={handleDone}
                style={{
                  padding: '8px 16px',
                  background: doneClicked
                    ? '#9E9E9E'
                    : (loading || !students.some(s => s.first_name))
                      ? '#ccc'
                      : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: '700',
                  cursor: (loading || !students.some(s => s.first_name) || doneClicked) ? 'not-allowed' : 'pointer',
                  boxShadow: doneClicked ? 'none' : (loading || !students.some(s => s.first_name)) ? 'none' : '0 2px 6px rgba(245, 158, 11, 0.3)',
                  opacity: doneClicked ? 0.6 : 1,
                  transition: 'all 0.2s ease'
                }}
                disabled={loading || !students.some(s => s.first_name)}
                onMouseEnter={(e) => !doneClicked && !e.currentTarget.disabled && (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <i className="fa-solid fa-check"></i> {doneClicked ? 'Completed' : '1. Mark Absent'}
              </button>
              )}
              {/* Unmark Absent button — reverses "1. Mark Absent". Clears the
                  placeholder fields (first name ".", marks "0", comment
                  "Absent") back to empty for students marked absent. Enabled
                  only when at least one such placeholder row exists. */}
              {!isReadOnly && (
              <button
                onClick={handleUnmarkAbsent}
                style={{
                  padding: '8px 16px',
                  background: (loading || !students.some(isAbsentMarked))
                    ? '#ccc'
                    : 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: '700',
                  cursor: (loading || !students.some(isAbsentMarked)) ? 'not-allowed' : 'pointer',
                  boxShadow: (loading || !students.some(isAbsentMarked)) ? 'none' : '0 2px 6px rgba(244, 63, 94, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                disabled={loading || !students.some(isAbsentMarked)}
                title="Undo the absent marking: reset first name '.', marks '0', and the 'Absent' comment back to empty"
                onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <i className="fa-solid fa-rotate-left"></i> Unmark Absent
              </button>
              )}
              {/* Copy Results button */}
              {!isReadOnly && (
              <button
                onClick={handleCopyResults}
                style={{
                  padding: '8px 12px',
                  background: copyClicked
                    ? '#9E9E9E'
                    : showCopiedMessage
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : !students.some(s => s.first_name)
                        ? '#ccc'
                        : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  cursor: (!students.some(s => s.first_name) || copyClicked) ? 'not-allowed' : 'pointer',
                  boxShadow: copyClicked ? 'none' : !students.some(s => s.first_name) ? 'none' : showCopiedMessage ? '0 2px 6px rgba(16, 185, 129, 0.3)' : '0 2px 6px rgba(59, 130, 246, 0.3)',
                  opacity: copyClicked ? 0.6 : 1,
                  transition: 'all 0.2s ease'
                }}
                disabled={!students.some(s => s.first_name)}
                title="Copy Results"
                onMouseEnter={(e) => !copyClicked && !e.currentTarget.disabled && (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                {copyClicked ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 'bold' }}>
                    <i className="fa-solid fa-circle-check"></i> Copied
                  </span>
                ) : showCopiedMessage ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 'bold' }}>
                    <i className="fa-solid fa-circle-check"></i> Copied!
                  </span>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <i className="fa-solid fa-copy"></i> 2. Copy to Edupage
                  </span>
                )}
              </button>
              )}
              {/* View All Comments — opens a modal listing every student's
                  comment at once, mirroring the button in the Checking tab. */}
              <button
                onClick={() => setShowAllComments(true)}
                style={{
                  padding: '8px 12px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(139, 92, 246, 0.3)',
                  transition: 'all 0.2s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                title="View all students' comments in one place"
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              >
                <i className="fa-solid fa-comments"></i> View All Comments
              </button>
              {/* "3. Ready to Print" was removed — the scans-folder save now
                  happens as the FIRST step of the Approve flow in
                  AssessmentTab (folder picker → student-list picker → submit).
                  No StudentsTab button needed anymore. */}
            </div>
          </div>
          
          {loading ? (
            <p>Loading students...</p>
          ) : students.length === 0 ? (
            <p className="no-data">No students found in this list.</p>
          ) : (
            <div className="students-content-wrapper">
              <div className="students-left">
                <div style={{
                  background: 'var(--card-bg, white)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid var(--border-color, #e0e0e0)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}>
                  <table className="students-table" style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.85rem'
                  }}>
                    <thead>
                      <tr style={{ background: 'var(--table-header-bg, linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%))', borderBottom: '2px solid var(--border-color, #dee2e6)' }}>
                        {!isReadOnly && (
                        <th style={{width: '4%', padding: '12px 8px', textAlign: 'center'}}>
                          <input
                            type="checkbox"
                            checked={students.length > 0 && selectedStudentIds.length === students.length}
                            onChange={handleSelectAllStudents}
                            style={{ cursor: 'pointer' }}
                            title="Select all"
                          />
                        </th>
                        )}
                        <th style={{width: '5%', padding: '12px 8px', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--table-header-color, #495057)'}}>#</th>
                        <th 
                          style={{width: '30%', padding: '12px 8px', cursor: 'pointer', userSelect: 'none', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--table-header-color, #495057)'}} 
                          onClick={() => handleSort('student_name')}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <i className="fa-solid fa-user" style={{ fontSize: '11px', color: 'var(--table-icon-color, #6c757d)' }}></i>
                            Student Name {sortConfig.key === 'student_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                          </div>
                        </th>
                        <th 
                          style={{width: '25%', padding: '12px 8px', cursor: 'pointer', userSelect: 'none', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--table-header-color, #495057)'}} 
                          onClick={() => handleSort('first_name')}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <i className="fa-solid fa-signature" style={{ fontSize: '11px', color: 'var(--table-icon-color, #6c757d)' }}></i>
                            First Name (Checked) {sortConfig.key === 'first_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                          </div>
                        </th>
                        <th 
                          style={{width: '10%', padding: '12px 8px', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--table-header-color, #495057)'}} 
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'space-between' }}>
                            <div 
                              style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none' }}
                              onClick={() => handleSort('marks')}
                            >
                              <i className="fa-solid fa-star" style={{ fontSize: '11px', color: 'var(--table-icon-color, #6c757d)' }}></i>
                              Marks {sortConfig.key === 'marks' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                            </div>
                            <button
                              onClick={handleCopyMarks}
                              disabled={!students.some(s => s.marks)}
                              style={{
                                background: !students.some(s => s.marks) ? '#ccc' : 'linear-gradient(135deg, #10B981, #059669)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                cursor: !students.some(s => s.marks) ? 'not-allowed' : 'pointer',
                                fontSize: '10px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                boxShadow: !students.some(s => s.marks) ? 'none' : '0 1px 3px rgba(16, 185, 129, 0.3)',
                                transition: 'all 0.2s ease'
                              }}
                              title="Copy all marks"
                              onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.transform = 'scale(1.05)')}
                              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                              <i className="fa-solid fa-copy"></i>
                            </button>
                          </div>
                        </th>
                        <th 
                          style={{width: '20%', padding: '12px 8px', cursor: 'pointer', userSelect: 'none', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--table-header-color, #495057)'}} 
                          onClick={() => handleSort('comments')}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <i className="fa-solid fa-comment" style={{ fontSize: '11px', color: 'var(--table-icon-color, #6c757d)' }}></i>
                            Comments {sortConfig.key === 'comments' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                          </div>
                        </th>
                        <th style={{width: '5%', padding: '12px 8px', textAlign: 'center', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--table-header-color, #495057)'}} title="EduPage points — 'a' marks an absent student. Copied to EduPage with the marks & comments.">
                          Points
                        </th>
                        <th style={{width: '5%', padding: '12px 8px', textAlign: 'center', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--table-header-color, #495057)'}} title="View scan image">
                          <i className="fa-solid fa-image" style={{ fontSize: '11px' }}></i>
                        </th>
                        <th style={{width: '5%', padding: '12px 8px'}}></th>
                        <th style={{width: '5%', padding: '12px 8px'}}></th>
                        {!isReadOnly && (
                        <th style={{width: '5%', padding: '12px 8px', textAlign: 'center', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--table-header-color, #495057)'}}>
                          <i className="fa-solid fa-arrow-right" style={{ fontSize: '11px' }}></i>
                        </th>
                        )}
                        {!isReadOnly && (
                        <th style={{width: '5%', padding: '12px 8px', textAlign: 'center', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--table-header-color, #495057)'}}>
                          <i className="fa-solid fa-trash" style={{ fontSize: '11px' }}></i>
                        </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {getSortedStudents().map((student, index) => {
                        const hasMismatch = checkNameMismatch(student.student_name, student.first_name);
                        return (
                    <tr key={student.id} style={{ 
                      background: hasMismatch ? 'var(--warning-bg, #fff3cd)' : 'transparent',
                      borderBottom: '1px solid var(--border-light, #f0f0f0)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!hasMismatch) e.currentTarget.style.background = 'var(--hover-bg, #f8f9fa)';
                    }}
                    onMouseLeave={(e) => {
                      if (!hasMismatch) e.currentTarget.style.background = 'transparent';
                    }}>
                      {!isReadOnly && (
                      <td style={{ textAlign: 'center', padding: '10px 8px' }}>
                        <input
                          type="checkbox"
                          checked={selectedStudentIds.includes(student.id)}
                          onChange={() => handleSelectStudent(student.id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      )}
                      <td style={{ padding: '10px 8px', fontSize: '0.8rem', color: 'var(--table-text-muted, #6c757d)', fontWeight: '600' }}>{index + 1}</td>
                      {/* Editable cells: double-click → input → Enter or
                          blur commits → Escape cancels. The commit handler
                          PUTs to /students/:id and updates local state. */}
                      {(() => {
                        const editingInputStyle = {
                          width: '100%',
                          padding: '4px 6px',
                          fontSize: 'inherit',
                          fontFamily: 'inherit',
                          border: '2px solid #3b82f6',
                          borderRadius: 4,
                          background: 'var(--input-bg, white)',
                          color: 'var(--text-primary)'
                        };
                        const renderEditable = (field, baseStyle, displayValue, displayPlaceholder = '-') => {
                          if (isReadOnly) {
                            return (
                              <td style={baseStyle}>
                                {(displayValue && String(displayValue).trim()) ? displayValue : displayPlaceholder}
                              </td>
                            );
                          }
                          const isEditing = editingCell?.studentId === student.id && editingCell?.field === field;
                          return (
                            <td
                              style={{ ...baseStyle, cursor: 'text' }}
                              title="Double-click to edit"
                              onDoubleClick={() => setEditingCell({ studentId: student.id, field, value: student[field] || '' })}
                            >
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editingCell.value}
                                  autoFocus
                                  onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                  onBlur={commitCellEdit}
                                  onKeyDown={handleCellKeyDown}
                                  style={editingInputStyle}
                                />
                              ) : (
                                (displayValue && String(displayValue).trim()) ? displayValue : displayPlaceholder
                              )}
                            </td>
                          );
                        };
                        return (
                          <>
                            {renderEditable('student_name',
                              { padding: '10px 8px', fontSize: '0.85rem', fontWeight: '500' },
                              student.student_name)}
                            {renderEditable('first_name',
                              { padding: '10px 8px', fontSize: '0.85rem' },
                              student.first_name)}
                            {renderEditable('marks',
                              (() => {
                                const over = marksExceedTotal(student.marks, student.comments);
                                return {
                                  padding: '10px 8px', fontSize: '0.85rem', fontWeight: '600',
                                  color: over ? '#b91c1c' : (student.marks && student.marks !== '-' ? '#10b981' : '#9ca3af'),
                                  background: over ? 'rgba(220, 38, 38, 0.18)' : undefined,
                                  boxShadow: over ? 'inset 0 0 0 2px rgba(220, 38, 38, 0.55)' : undefined
                                };
                              })(),
                              student.marks)}
                            {renderEditable('comments',
                              { padding: '10px 8px', fontSize: '0.82rem', color: 'var(--table-text-secondary, #6b7280)' },
                              student.comments)}
                          </>
                        );
                      })()}
                      {/* Points — 'a' for an absent student (no real comment),
                          blank otherwise. Copied to EduPage alongside marks. */}
                      <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 700, color: '#dc2626' }}>
                        {isStudentAbsent(student) ? 'a' : ''}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                        {(() => {
                          const imgMatch = (student.comments || '').match(/\[([^\]]+\.(?:jpg|jpeg|png|gif))[\]]/i);
                          const imgName = imgMatch ? imgMatch[1] : null;
                          if (!imgName) return <span style={{ color: '#9ca3af', fontSize: '0.7rem' }}>—</span>;
                          return (
                            <button
                              onClick={() => handleFaultPreviewImage(student.comments)}
                              disabled={faultPreviewLoading === imgName}
                              title={`View scan image (${imgName})`}
                              style={{
                                background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                width: '32px',
                                height: '32px',
                                cursor: faultPreviewLoading === imgName ? 'wait' : 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '13px',
                                boxShadow: '0 2px 4px rgba(124, 58, 237, 0.3)',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.transform = 'scale(1.1)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                            >
                              {faultPreviewLoading === imgName
                                ? <i className="fa-solid fa-spinner fa-spin" />
                                : <i className="fa-solid fa-image" />}
                            </button>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '10px 8px' }}></td>
                      <td style={{ padding: '10px 8px' }}></td>
                      {!isReadOnly && (
                      <td style={{ textAlign: 'center', padding: '10px 8px' }}>
                        <button
                          className="btn-move-to-unmatched"
                          onClick={() => handleSendToUnmatched(student)}
                          title="Move to Unmatched List"
                          disabled={!student.first_name && !student.marks && !student.comments}
                          style={{ 
                            background: (student.first_name || student.marks || student.comments) ? 'linear-gradient(135deg, #9C27B0 0%, #7b1fa2 100%)' : '#ccc', 
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            width: '32px',
                            height: '32px',
                            cursor: (student.first_name || student.marks || student.comments) ? 'pointer' : 'not-allowed',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '13px',
                            boxShadow: (student.first_name || student.marks || student.comments) ? '0 2px 4px rgba(156, 39, 176, 0.3)' : 'none',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (student.first_name || student.marks || student.comments) {
                              e.currentTarget.style.transform = 'scale(1.1)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <i className="fa-solid fa-arrow-right"></i>
                        </button>
                      </td>
                      )}
                      {!isReadOnly && (
                      <td style={{ textAlign: 'center', padding: '10px 8px' }}>
                        <button
                          className="btn-delete-row"
                          onClick={() => handleDeleteStudent(student.id)}
                          title="Delete student"
                          style={{ 
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', 
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            width: '32px',
                            height: '32px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '13px',
                            boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </td>
                      )}
                    </tr>
                    );
                  })}
                      </tbody>
                  </table>
                </div>
              </div>
              
              {/* Unmatched/Fault Students Section - Shows checked papers that couldn't match */}
              {console.log('Rendering check: faultStudents.length =', faultStudents.length)}
              {faultStudents.length > 0 && (
              <div className="students-right">
                <div style={{
                  background: 'var(--card-bg, white)',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid var(--warning-border, #fbbf24)',
                  boxShadow: '0 2px 8px rgba(245, 158, 11, 0.15)'
                }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '8px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    color: 'white'
                  }}>
                    <i className="fa-solid fa-exclamation-triangle"></i>
                  </div>
                  <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#d97706' }}>
                    Unmatched Papers ({faultStudents.length})
                  </h4>
                </div>
                {!isReadOnly && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button
                    onClick={handleMatchAllUnmatched}
                    disabled={loading}
                    style={{
                      background: loading ? '#ccc' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '8px 14px',
                      borderRadius: '8px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '0.8rem',
                      boxShadow: loading ? 'none' : '0 2px 6px rgba(16, 185, 129, 0.3)',
                      transition: 'all 0.2s ease'
                    }}
                    title="Automatically match all"
                    onMouseEnter={(e) => !loading && (e.currentTarget.style.transform = 'scale(1.05)')}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <i className="fa-solid fa-check-double"></i> Match All
                  </button>
                  <button
                    onClick={handleSaveAllToMistakes}
                    disabled={loading}
                    style={{
                      background: loading ? '#ccc' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '8px 14px',
                      borderRadius: '8px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '0.8rem',
                      boxShadow: loading ? 'none' : '0 2px 6px rgba(245, 158, 11, 0.3)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => !loading && (e.currentTarget.style.transform = 'scale(1.05)')}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <i className="fa-solid fa-arrow-right"></i> Save to Mistakes
                  </button>
                  <button
                    onClick={handleClearAllUnmatched}
                    style={{
                      background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '8px 14px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.8rem',
                      boxShadow: '0 2px 6px rgba(107, 114, 128, 0.3)',
                      transition: 'all 0.2s ease'
                    }}
                    title="Clear all unmatched from view"
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <i className="fa-solid fa-xmark"></i> Clear All
                  </button>
                </div>
                )}
              </div>
              <div style={{
                marginBottom: '12px',
                padding: '10px 12px',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderRadius: '8px',
                border: '1px solid #fbbf24',
                fontSize: '0.75rem',
                lineHeight: '1.5',
                color: '#78350f'
              }}>
                <i className="fa-solid fa-thumbtack" style={{ marginRight: '6px' }}></i>These papers couldn't be matched. Includes <strong>duplicates</strong> and <strong>not found</strong> entries. Enter the correct roll number or save to Mistakes.
              </div>
              <div style={{
                background: '#f9fafb',
                borderRadius: '8px',
                padding: '8px',
                border: '1px solid #e5e7eb'
              }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.8rem'
              }}>
                <thead>
                  <tr style={{ background: 'white' }}>
                    <th style={{ width: '5%', padding: '8px 6px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', borderBottom: '2px solid #e5e7eb' }}>#</th>
                    <th style={{ width: '25%', padding: '8px 6px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', borderBottom: '2px solid #e5e7eb' }}>Checked Name</th>
                    <th style={{ width: '12%', padding: '8px 6px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', borderBottom: '2px solid #e5e7eb' }}>Marks</th>
                    <th style={{ width: '15%', padding: '8px 6px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', borderBottom: '2px solid #e5e7eb' }}>Target Roll No.</th>
                    <th style={{ width: '18%', padding: '8px 6px', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', borderBottom: '2px solid #e5e7eb' }}>Match</th>
                    <th style={{ width: '8%', padding: '8px 6px', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', borderBottom: '2px solid #e5e7eb' }}>View</th>
                    <th style={{ width: '8%', padding: '8px 6px', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', borderBottom: '2px solid #e5e7eb' }}>Del</th>
                  </tr>
                </thead>
                <tbody>
                  {faultStudents.map((fault, idx) => {
                    // Check if this is a duplicate by looking for same roll number
                    const studentName = fault.student_name || fault.name || '';
                    const rollMatch = studentName.match(/\(([^)]+)\)/);
                    const rollNumber = rollMatch ? rollMatch[1].trim().toLowerCase() : null;
                    const isDuplicate = rollNumber && faultStudents.filter(f => {
                      const fName = (f.student_name || f.name || '');
                      const fMatch = fName.match(/\(([^)]+)\)/);
                      return fMatch && fMatch[1].trim().toLowerCase() === rollNumber;
                    }).length > 1;
                    
                    return (
                      <tr key={idx} style={{
                        background: isDuplicate ? 'rgba(239, 68, 68, 0.08)' : 'white',
                        transition: 'background 0.2s ease'
                      }}>
                        <td style={{ padding: '10px 6px', borderBottom: '1px solid #f3f4f6', fontSize: '0.8rem', color: '#6b7280' }}>{idx + 1}</td>
                        <td style={{ padding: '10px 6px', borderBottom: '1px solid #f3f4f6', fontSize: '0.8rem', color: '#111827' }}>
                          <div style={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            maxWidth: '150px',
                            fontWeight: '500'
                          }}>
                            {fault.student_name || fault.name}
                          </div>
                          {isDuplicate && <span style={{
                            display: 'inline-block',
                            marginLeft: '4px',
                            padding: '2px 6px',
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            color: 'white',
                            fontSize: '0.65rem',
                            borderRadius: '4px',
                            fontWeight: 'bold'
                          }}>DUP</span>}
                        </td>
                        <td style={{ padding: '10px 6px', borderBottom: '1px solid #f3f4f6', fontSize: '0.8rem', color: '#111827', fontWeight: '600' }}>{fault.marks}</td>
                        <td style={{ padding: '10px 6px', borderBottom: '1px solid #f3f4f6' }}>
                          {!isReadOnly && (
                          <input
                            type="text"
                            placeholder="e.g. A01"
                            value={faultCorrections[idx] || ''}
                            onChange={(e) => setFaultCorrections({
                              ...faultCorrections,
                              [idx]: e.target.value
                            })}
                            style={{
                              padding: '6px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              width: '100%',
                              fontSize: '0.8rem',
                              background: 'white',
                              color: '#111827',
                              transition: 'border 0.2s ease'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                          />
                          )}
                        </td>
                        <td style={{ padding: '10px 6px', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                          {!isReadOnly && (
                          <button
                            onClick={() => handleCorrectFault(idx, faultCorrections[idx])}
                            disabled={!faultCorrections[idx] || loading}
                            style={{
                              padding: '6px 12px',
                              background: (!faultCorrections[idx] || loading) ? '#ccc' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: (!faultCorrections[idx] || loading) ? 'not-allowed' : 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              boxShadow: (!faultCorrections[idx] || loading) ? 'none' : '0 2px 4px rgba(16, 185, 129, 0.3)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.transform = 'scale(1.05)')}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          >
                            Match
                          </button>
                          )}
                        </td>
                        <td style={{ padding: '10px 6px', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                          {(() => {
                            const imgMatch = (fault.comments || '').match(/\[([^\]]+\.(?:jpg|jpeg|png|gif))[\]]/i);
                            const imgName = imgMatch ? imgMatch[1] : null;
                            return imgName ? (
                              <button
                                onClick={() => handleFaultPreviewImage(fault.comments)}
                                disabled={faultPreviewLoading === imgName}
                                title="View scan image"
                                style={{
                                  background: '#7C3AED', color: 'white', border: 'none',
                                  borderRadius: '6px', width: '28px', height: '28px',
                                  cursor: 'pointer', fontSize: '0.85rem',
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  boxShadow: '0 2px 4px rgba(124,58,237,0.3)', transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                              >
                                <i className="fa-solid fa-image" />
                              </button>
                            ) : <span style={{ color: '#9ca3af', fontSize: '0.7rem' }}>—</span>;
                          })()}
                        </td>
                        <td style={{ padding: '10px 6px', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                          {!isReadOnly && (
                          <button
                            onClick={() => handleDeleteFault(fault.id, idx)}
                            title="Delete"
                            style={{
                              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              width: '28px',
                              height: '28px',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          >
                            ✕
                          </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
              )}
            </div>
          )}
        </div>
      )}
        </div>{/* close RIGHT pane */}
        {!selectedList && (
          <SubjectColumn
            items={lists}
            gradeFilter={gradeFilter}
            sectionFilter={sectionFilter}
            subjectFilter={subjectFilter}
            onSubject={(subject) => {
              setSelectedCardKeys([]);
              setSubjectFilter(subjectFilter === subject ? null : subject);
            }}
          />
        )}
      </div>{/* close two-column flex wrapper */}

      {/* Fault Image Preview Modal */}
      {faultPreviewImage && faultPreviewImage.images && faultPreviewImage.images.length > 0 && (() => {
        const total = faultPreviewImage.images.length;
        const current = faultPreviewImage.images[faultPreviewImage.index];
        const atStart = faultPreviewImage.index <= 0;
        const atEnd = faultPreviewImage.index >= total - 1;
        const goPrev = () => !atStart && setFaultPreviewImage(p => ({ ...p, index: p.index - 1 }));
        const goNext = () => !atEnd && setFaultPreviewImage(p => ({ ...p, index: p.index + 1 }));
        return (
          <div
            onClick={() => setFaultPreviewImage(null)}
            // When the View All Comments modal is open, dock the image to the
            // RIGHT side (left:44vw) so comments + image sit side-by-side,
            // mirroring the Checking tab. Otherwise full-screen as before.
            style={{
              position: 'fixed', top: 'var(--header-height, 52px)', bottom: 0, right: 0,
              left: showAllComments ? '44vw' : 0,
              background: showAllComments ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.88)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 9999, flexDirection: 'column', gap: '12px'
            }}
          >
            <div onClick={e => e.stopPropagation()} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12 }}>
              {total > 1 && (
                <button
                  onClick={goPrev}
                  disabled={atStart}
                  title="Previous image"
                  style={{
                    background: atStart ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.85)',
                    color: atStart ? 'rgba(255,255,255,0.5)' : '#111',
                    border: 'none', borderRadius: '50%',
                    width: 44, height: 44, fontSize: 20, fontWeight: 700,
                    cursor: atStart ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  <i className="fa-solid fa-chevron-left"></i>
                </button>
              )}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setFaultPreviewImage(null)}
                  style={{
                    position: 'absolute', top: -14, right: -14,
                    background: '#EF4444', color: 'white', border: 'none',
                    borderRadius: '50%', width: 32, height: 32,
                    cursor: 'pointer', fontWeight: '700', fontSize: 18, lineHeight: '30px',
                    zIndex: 1
                  }}
                >×</button>
                <p style={{ color: 'white', textAlign: 'center', marginBottom: 8, fontSize: 13 }}>
                  {current.name}
                  {total > 1 && (
                    <span style={{ marginLeft: 10, opacity: 0.75 }}>
                      ({faultPreviewImage.index + 1} / {total})
                    </span>
                  )}
                </p>
                <ZoomableImage url={current.url} alt={current.name} width={showAllComments ? '52vw' : '80vw'} height={showAllComments ? 'calc(100vh - var(--header-height, 52px) - 56px)' : 'calc(100vh - var(--header-height, 52px) - 80px)'} />
              </div>
              {total > 1 && (
                <button
                  onClick={goNext}
                  disabled={atEnd}
                  title="Next image"
                  style={{
                    background: atEnd ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.85)',
                    color: atEnd ? 'rgba(255,255,255,0.5)' : '#111',
                    border: 'none', borderRadius: '50%',
                    width: 44, height: 44, fontSize: 20, fontWeight: 700,
                    cursor: atEnd ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  <i className="fa-solid fa-chevron-right"></i>
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Add Student Modal */}
      {/* View All Comments modal — lists every student's one-line comment,
          mirroring the Checking tab's button. */}
      {showHistory && selectedList && (
        <div
          style={{
            position: 'fixed', top: 'var(--header-height, 52px)', left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)', display: 'flex',
            alignItems: 'center',
            justifyContent: (selectedHistoryBatch && faultPreviewImage && faultPreviewImage.images && faultPreviewImage.images.length > 0) ? 'flex-start' : 'center',
            zIndex: 1000, padding: '12px 16px', boxSizing: 'border-box'
          }}
          onClick={() => { setShowHistory(false); setSelectedHistoryBatch(null); setHistoryCopied(false); }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--panel-bg, white)', borderRadius: 'var(--radius-lg, 12px)',
              width: selectedHistoryBatch
                ? ((faultPreviewImage && faultPreviewImage.images && faultPreviewImage.images.length > 0) ? 'calc(58vw - 16px)' : '96%')
                : '90%',
              maxWidth: selectedHistoryBatch ? 'none' : '640px',
              maxHeight: 'calc(100vh - var(--header-height, 52px) - 32px)',
              display: 'flex', flexDirection: 'column',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)', overflow: 'hidden'
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '12px', padding: '18px 24px', borderBottom: '1px solid var(--border-color, #e5e7eb)'
            }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary, #111827)', fontSize: '1.05rem' }}>
                {selectedHistoryBatch ? 'Approved marks' : 'Approval history'}
              </h3>
              <button
                onClick={() => { setShowHistory(false); setSelectedHistoryBatch(null); setHistoryCopied(false); }}
                style={{
                  padding: '7px 14px', background: 'var(--surface, #f1f5f9)',
                  color: 'var(--text-primary)', border: '1px solid var(--border-color, #ddd)',
                  borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600
                }}
              >
                <i className="fa-solid fa-times"></i> Close
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
              {selectedHistoryBatch ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                    <button
                      onClick={() => { setSelectedHistoryBatch(null); setHistoryCopied(false); }}
                      style={{
                        padding: '6px 12px', background: 'transparent',
                        color: 'var(--text-primary, #374151)', border: '1px solid var(--border-color, #d1d5db)',
                        borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600
                      }}
                    >
                      <i className="fa-solid fa-arrow-left" style={{ marginRight: 6 }}></i>
                      Back
                    </button>
                    {!isReadOnly && (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        onClick={handleMarkHistoryAbsent}
                        disabled={historyAbsentBusy || !historyRowsInListOrder(selectedHistoryBatch).some(historyRowNeedsAbsent)}
                        style={{
                          padding: '8px 16px',
                          background: historyAbsentBusy || !historyRowsInListOrder(selectedHistoryBatch).some(historyRowNeedsAbsent)
                            ? '#ccc'
                            : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '0.85rem',
                          fontWeight: '700',
                          cursor: historyAbsentBusy || !historyRowsInListOrder(selectedHistoryBatch).some(historyRowNeedsAbsent) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <i className="fa-solid fa-check"></i> {historyAbsentBusy ? 'Marking…' : '1. Mark Absent'}
                      </button>
                      <button
                        onClick={handleCopyHistoryResults}
                        disabled={!historyRowsInListOrder(selectedHistoryBatch).some((row) => historyCheckedName(row))}
                        style={{
                          padding: '8px 12px',
                          background: historyCopied
                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                            : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          boxShadow: '0 2px 6px rgba(59, 130, 246, 0.3)'
                        }}
                      >
                        <i className="fa-solid fa-copy"></i> {historyCopied ? 'Copied' : '2. Copy to Edupage'}
                      </button>
                      </div>
                    )}
                  </div>
                  <p style={{ margin: '0 0 14px', color: 'var(--text-secondary, #4b5563)', fontSize: '0.9rem' }}>
                    <strong style={{ color: 'var(--text-primary, #111827)' }}>{selectedHistoryBatch.folder_name}</strong>
                    {' · '}
                    {formatHistoryDate(selectedHistoryBatch.approved_at)}
                    {' · '}
                    {selectedHistoryBatch.assignment_name || 'No assignment name'}
                  </p>
                  <div style={{
                    background: 'var(--card-bg, white)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '1px solid var(--border-color, #e0e0e0)'
                  }}>
                    <table className="students-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ background: 'var(--table-header-bg, linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%))', borderBottom: '2px solid var(--border-color, #dee2e6)' }}>
                          <th style={{ width: '5%', padding: '12px 8px', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--table-header-color, #495057)' }}>#</th>
                          <th style={{ width: '22%', padding: '12px 8px', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--table-header-color, #495057)' }}>
                            <i className="fa-solid fa-user" style={{ fontSize: '11px', marginRight: 6 }}></i>
                            Student Name
                          </th>
                          <th style={{ width: '22%', padding: '12px 8px', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--table-header-color, #495057)' }}>
                            <i className="fa-solid fa-signature" style={{ fontSize: '11px', marginRight: 6 }}></i>
                            First Name (Checked)
                          </th>
                          <th style={{ width: '10%', padding: '12px 8px', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--table-header-color, #495057)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                              <span>
                                <i className="fa-solid fa-star" style={{ fontSize: '11px', marginRight: 6 }}></i>
                                Marks
                              </span>
                              <button
                                onClick={handleCopyHistoryMarks}
                                disabled={!historyRowsInListOrder(selectedHistoryBatch).some((row) => row.marks)}
                                title="Copy all marks"
                                style={{
                                  background: historyRowsInListOrder(selectedHistoryBatch).some((row) => row.marks)
                                    ? 'linear-gradient(135deg, #10B981, #059669)'
                                    : '#ccc',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  cursor: historyRowsInListOrder(selectedHistoryBatch).some((row) => row.marks) ? 'pointer' : 'not-allowed',
                                  fontSize: '10px'
                                }}
                              >
                                <i className="fa-solid fa-copy"></i>
                              </button>
                            </div>
                          </th>
                          <th style={{ width: '28%', padding: '12px 8px', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--table-header-color, #495057)' }}>
                            <i className="fa-solid fa-comment" style={{ fontSize: '11px', marginRight: 6 }}></i>
                            Comments
                          </th>
                          <th style={{ width: '6%', padding: '12px 8px', textAlign: 'center', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--table-header-color, #495057)' }} title="EduPage points — 'a' marks an absent student.">
                            Points
                          </th>
                          <th style={{ width: '5%', padding: '12px 8px', textAlign: 'center' }} title="View scan image">
                            <i className="fa-solid fa-image" style={{ fontSize: '11px' }}></i>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyRowsInListOrder(selectedHistoryBatch).map((row, index) => {
                          const checkedName = historyCheckedName(row);
                          const hasMismatch = checkNameMismatch(row.student_name, row.first_name);
                          const over = marksExceedTotal(row.marks, row.comments);
                          const imgMatch = (row.comments || '').match(/\[([^\]]+\.(?:jpg|jpeg|png|gif))[\]]/i);
                          const imgName = imgMatch ? imgMatch[1] : null;
                          return (
                            <tr key={row.id} style={{
                              background: hasMismatch ? 'var(--warning-bg, #fff3cd)' : 'transparent',
                              borderBottom: '1px solid var(--border-light, #f0f0f0)'
                            }}>
                              <td style={{ padding: '10px 8px', fontSize: '0.8rem', color: 'var(--table-text-muted, #6c757d)', fontWeight: '600' }}>{index + 1}</td>
                              <td style={{ padding: '10px 8px', fontSize: '0.85rem', fontWeight: '500' }}>{row.student_name || '-'}</td>
                              <td style={{ padding: '10px 8px', fontSize: '0.85rem' }}>{checkedName || '-'}</td>
                              <td style={{
                                padding: '10px 8px', fontSize: '0.85rem', fontWeight: '600',
                                color: over ? '#b91c1c' : (row.marks && row.marks !== '-' ? '#10b981' : '#9ca3af'),
                                background: over ? 'rgba(220, 38, 38, 0.18)' : undefined,
                                boxShadow: over ? 'inset 0 0 0 2px rgba(220, 38, 38, 0.55)' : undefined
                              }}>{row.marks == null || row.marks === '' ? '-' : row.marks}</td>
                              <td style={{ padding: '10px 8px', fontSize: '0.82rem', color: 'var(--table-text-secondary, #6b7280)', whiteSpace: 'pre-wrap' }}>{row.comments || '-'}</td>
                              <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 700, color: '#dc2626' }}>
                                {isStudentAbsent(row) ? 'a' : ''}
                              </td>
                              <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                                {imgName ? (
                                  <button
                                    onClick={() => handleFaultPreviewImage(row.comments)}
                                    disabled={faultPreviewLoading === imgName}
                                    title={`View scan image (${imgName})`}
                                    style={{
                                      background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      width: '32px',
                                      height: '32px',
                                      cursor: faultPreviewLoading === imgName ? 'wait' : 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '13px'
                                    }}
                                  >
                                    {faultPreviewLoading === imgName
                                      ? <i className="fa-solid fa-spinner fa-spin" />
                                      : <i className="fa-solid fa-image" />}
                                  </button>
                                ) : <span style={{ color: '#9ca3af', fontSize: '0.7rem' }}>—</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : historyLoading ? (
                <p style={{ color: 'var(--text-secondary, #6b7280)' }}>Loading approval history…</p>
              ) : historyError ? (
                <p style={{ color: '#b91c1c' }}>{historyError}</p>
              ) : historyBatches.length === 0 ? (
                <p style={{ color: 'var(--text-secondary, #6b7280)' }}>No approval history yet.</p>
              ) : (
                historyBatches.map((batch) => (
                  <button
                    key={batch.batch}
                    type="button"
                    onClick={() => { setHistoryCopied(false); setSelectedHistoryBatch(batch); }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      background: 'var(--card-bg, #fff)', border: '1px solid var(--border-color, #e5e7eb)',
                      borderRadius: 12, padding: '12px 14px', marginBottom: 8, cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontWeight: 800, color: 'var(--text-primary, #111827)' }}>{batch.folder_name}</div>
                    <div style={{ marginTop: 4, fontSize: 13, color: 'var(--text-secondary, #6b7280)' }}>
                      {formatHistoryDate(batch.approved_at)}
                      {' · '}
                      {batch.assignment_name || 'No assignment name'}
                      {' · '}
                      {batch.students.length} student{batch.students.length === 1 ? '' : 's'}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {showAllComments && (
        <div
          style={{
            position: 'fixed', top: 'var(--header-height, 52px)', left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)', display: 'flex',
            alignItems: 'center',
            // When an image is open, dock this modal to the LEFT half so the
            // image can sit beside it on the right (side-by-side view).
            justifyContent: (faultPreviewImage && faultPreviewImage.images && faultPreviewImage.images.length > 0) ? 'flex-start' : 'center',
            zIndex: 1000,
            padding: '12px 16px',
            boxSizing: 'border-box'
          }}
          onClick={() => setShowAllComments(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={(faultPreviewImage && faultPreviewImage.images && faultPreviewImage.images.length > 0) ? {
              background: 'var(--panel-bg)', borderRadius: 'var(--radius-lg)',
              width: 'calc(42vw - 16px)', maxWidth: 'none', maxHeight: 'calc(100vh - var(--header-height, 52px) - 32px)',
              margin: '16px 0 16px 16px',
              display: 'flex', flexDirection: 'column',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)', overflow: 'hidden'
            } : {
              background: 'var(--panel-bg)', borderRadius: 'var(--radius-lg)',
              width: '90%', maxWidth: '900px', maxHeight: '85vh',
              display: 'flex', flexDirection: 'column',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)', overflow: 'hidden'
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '12px', padding: '18px 24px', borderBottom: '1px solid var(--border-color, #e5e7eb)'
            }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>
                All Comments ({students.length})
              </h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={async () => {
                    const text = students.map(s => {
                      const c = (s.comments && s.comments.trim() && s.comments !== '-') ? s.comments : 'Absent';
                      const m = (s.marks != null && s.marks !== '') ? ` [${s.marks}]` : '';
                      return `${s.student_name || ''}${m}: ${c}`;
                    }).join('\n\n');
                    try { await navigator.clipboard.writeText(text); } catch (e) { console.warn('Copy failed:', e); }
                  }}
                  style={{
                    padding: '7px 14px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                    color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer',
                    fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6
                  }}
                >
                  <i className="fa-solid fa-copy"></i> Copy All
                </button>
                <button
                  onClick={() => setShowAllComments(false)}
                  style={{
                    padding: '7px 14px', background: 'var(--surface, #f1f5f9)',
                    color: 'var(--text-primary)', border: '1px solid var(--border-color, #ddd)',
                    borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600
                  }}
                >
                  <i className="fa-solid fa-times"></i> Close
                </button>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
              {students.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>No students in this list.</p>
              ) : (
                students.map((s, idx) => {
                  const c = (s.comments && s.comments.trim() && s.comments !== '-') ? s.comments : '';
                  const isAbsent = !c;
                  // Same image-name parse the list rows use, so the scan-image
                  // button opens the existing faultPreviewImage modal (zIndex
                  // 9999 — layers above this modal).
                  const imgMatch = (s.comments || '').match(/\[([^\]]+\.(?:jpg|jpeg|png|gif))[\]]/i);
                  const imgName = imgMatch ? imgMatch[1] : null;
                  return (
                    <div
                      key={s.id || idx}
                      style={{
                        padding: '12px 14px', marginBottom: '10px', borderRadius: '10px',
                        border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--surface, #fafafa)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: c ? 6 : 0 }}>
                        <strong style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                          {idx + 1}. {s.student_name || '(no name)'}
                        </strong>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                          {imgName && (
                            <button
                              onClick={() => handleFaultPreviewImage(s.comments)}
                              disabled={faultPreviewLoading === imgName}
                              title={`View scan image (${imgName})`}
                              style={{
                                background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                                color: 'white', border: 'none', borderRadius: '6px',
                                width: '30px', height: '30px',
                                cursor: faultPreviewLoading === imgName ? 'wait' : 'pointer',
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px'
                              }}
                            >
                              {faultPreviewLoading === imgName
                                ? <i className="fa-solid fa-spinner fa-spin" />
                                : <i className="fa-solid fa-image" />}
                            </button>
                          )}
                          <span style={{ fontSize: '0.8rem', fontWeight: 700, color: isAbsent ? '#9ca3af' : '#059669' }}>
                            {isAbsent ? 'Absent' : (s.marks != null && s.marks !== '' ? s.marks : '-')}
                          </span>
                        </div>
                      </div>
                      {c && (
                        <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.4 }}>
                          {c}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {!isReadOnly && showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--panel-bg)',
            padding: '30px',
            borderRadius: 'var(--radius-lg)',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: 'var(--text-primary)' }}>Add New Student</h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Student Name (with Roll No) *</label>
                <input
                  type="text"
                  placeholder="e.g., (A01) John Doe"
                  value={newStudent.student_name}
                  onChange={(e) => setNewStudent({ ...newStudent, student_name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '14px',
                    borderRadius: 'var(--radius-md)',
                    border: '2px solid var(--border)',
                    background: 'var(--surface)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>First Name (Checked)</label>
                <input
                  type="text"
                  placeholder="Optional"
                  value={newStudent.first_name}
                  onChange={(e) => setNewStudent({ ...newStudent, first_name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '14px',
                    borderRadius: 'var(--radius-md)',
                    border: '2px solid var(--border)',
                    background: 'var(--surface)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Marks (Optional)</label>
                  <input
                    type="text"
                    placeholder="Optional"
                    value={newStudent.marks}
                    onChange={(e) => setNewStudent({ ...newStudent, marks: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '14px',
                      borderRadius: 'var(--radius-md)',
                      border: '2px solid var(--border)',
                      background: 'var(--surface)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>
                
                <div style={{ flex: 2 }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Comments (Optional)</label>
                  <input
                    type="text"
                    placeholder="Optional"
                    value={newStudent.comments}
                    onChange={(e) => setNewStudent({ ...newStudent, comments: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '14px',
                      borderRadius: 'var(--radius-md)',
                      border: '2px solid var(--border)',
                      background: 'var(--surface)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewStudent({ student_name: '', first_name: '', marks: '', comments: '' });
                }}
                style={{
                  padding: '10px 20px',
                  background: 'var(--surface)',
                  color: 'var(--text-primary)',
                  border: '2px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddStudent}
                disabled={!newStudent.student_name || loading}
                style={{
                  padding: '10px 20px',
                  background: newStudent.student_name ? '#9C27B0' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: newStudent.student_name ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Add Student
              </button>
            </div>
          </div>
        </div>
      )}

      {/* The "Select Scans Folder" modal was removed — the scans-folder
          save now happens inside the AssessmentTab Approve flow. */}

      <MessageBox
        message={message} 
        onClose={() => setMessage({ show: false, text: '', type: 'info' })} 
      />
      
      <ConfirmDialog 
        confirm={confirm} 
        onConfirm={handleConfirm} 
        onCancel={handleCancel} 
      />
    </div>
  );
};

  

export default StudentsTab;
